import { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import {
  ArrowLeft,
  Pencil,
  Check,
  X,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Pause,
  Clock,
  Info,
  Printer,
  Copy,
  Download,
} from 'lucide-react';
import {
  Mic,
  Sparkle,
  PhotoFilter,
  ThumbDown,
  ThumbUp,
  Medications,
  SaveDraft,
  SaveRecord,
} from '@/assets/icons';
import { WhatsApp } from '@/assets/icons/WhatsApp';

const showWhatsAppAction = import.meta.env.VITE_ENABLE_WHATSAPP === 'true';

import playIcon from '@/assets/Vector.svg';
import { Button } from '@/components/base/Button';
import { Textarea } from '@/components/base/Form/Textarea';
import { Input } from '@/components/base/Form/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/base/Form/Select';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/base/Resize';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/base/Accordion';
import { AccountCircle } from '@/assets/icons';
import PranikLogo from '@/assets/logo.svg';
import { CDSSMedicationCard } from '@/components/consultation/CDSSMedicationCard';
import { LabReportsModal } from '@/components/consultation/LabReportsModal';
import { SmartVoiceNote } from '@/components/consultation/SmartVoiceNote';
import { ReferenceSlokaCard } from '@/ayurveda/components/ReferenceSlokaCard';
import { summarizeChiefComplaint } from '@/ayurveda/utils/parseAyurveda';
import type { PersonalHistory, PrescribedMedicine } from '@/ayurveda/types/ayurveda';
import { MedicinesTable } from '@/components/consultation/paData/TreatmentCard';
import { VitalsForm } from '@/components/consultation/VitalsForm';
import { useAudioStore, formatAudioDuration } from '@/lib/stores/audioStore';
import { useConsultationRecovery } from '@/lib/hooks/useConsultationRecovery';
import type { VoiceNoteState } from '@/lib/hooks/useVoiceNote';
import type {
  TranscriptionSegment,
  CDSSSuggestion,
  VitalSigns,
  AIAnalysisResult,
  AIAnalysisField,
  ConsultationStatus,
  ReferenceSlokaItem,
  BetterMedicationSuggestion,
  DrugInteractionAlert,
  MissedSymptomAlert,
  AllergyConflictAlert,
} from '@/lib/types/consultation';
import { formatVitals } from '@/lib/types/consultation';
import { formatGenderDisplay } from '@/lib/types/patient';
import { logger } from '@/lib/utils/logger';
import { printPrescription, type PrescriptionData } from '@/lib/utils/prescriptionGenerator';
import { splitInvestigationsText } from '@/lib/utils/caseSheetSummary';
import {
  proseTextToHtmlList,
  splitCommaSeparatedText,
  splitMedicalNarrativeToBullets,
} from '@/lib/utils/clinicalText';
import { generatePrescription, sendPrescription } from '@/lib/api/prescription';
import { useAuthStore } from '@/lib/stores/authStore';
import { useDepartmentConfig } from '@/lib/hooks/useDepartmentConfig';
import { toast } from 'sonner';
import type { StructuredMedication } from '@/lib/utils/medications';
import {
  formatDiagnosisEntry,
  formatInvestigationEntry,
  formatRelationConditionEntry,
  formatRelationConditionList,
  getFamilyHistoryItems,
  isRelationConditionItem,
  sanitizeClinicalHtml,
  splitLabelValueItem,
  type RelationConditionEntry,
} from '@/lib/utils/clinicalText';

// Generic case-sheet sections hidden for Ayurveda only (every other
// department keeps them unchanged). Either superseded by an Ayurveda-specific
// section (History covers Family History/Family-Social-Occupational; General
// Examination is Ayurveda's renamed Vitals, not this generic section) or not
// wanted on the Ayurveda case sheet at all. Checked in TWO places below,
// since a field can reach the render loop either as a standalone
// effectiveFieldOrder entry or nested inside a group's groupFields array
// (e.g. "Other History") — both need this same list to avoid a field
// slipping through via whichever path the backend's field-order config uses.
const AYURVEDA_HIDDEN_SECTIONS = new Set([
  'assessmentDiagnosis',
  'vitals',
  'familyHistory',
  'socialHistory',
  'medicalHistory', // rendered as its own top-level "History" section instead
  'otherHistory', // group heading left empty once its fields above are hidden/moved
  'clinicalSymptoms',
  'investigations',
  'allergies',
  'treatmentPlan', // generic "Treatment" group (medication table) — superseded by the Ayurveda-specific standalone "Treatment" section (Medicines/Treatment Principle/Treatment Given)
  'diet', // rendered as a row nested inside the Treatment section instead of its own section
  'lifestyle', // rendered as a row nested inside the Treatment section instead of its own section
]);

export type SimplifiedMedication = StructuredMedication;

export type DiagnosisCodeItem = {
  symptom: string;
  code: string;
  description: string;
  /** Coding system the code belongs to (e.g. "ICD-10", "ICD-11"), as reported by the AI summary. */
  type?: string;
};

const EMPTY_PERSONAL_HISTORY: PersonalHistory = {
  diet: null,
  appetite: null,
  sleep: null,
  exercise: null,
  bowel: null,
  bladder: null,
  addiction: null,
  menstrual: null,
};

/** Merges Personal History per-field (not whole-object): `edited` is seeded
 *  once from the initial data and can be frozen (by the page-level sync
 *  guard) before slower-to-arrive data — e.g. Diet backfilled from
 *  commonly_eaten_food streaming in later — ever reaches it. A real edit
 *  (non-null in `edited`) still wins per field; an untouched field still
 *  falls through to the fresher `base`. */
const mergePersonalHistory = (
  edited: PersonalHistory | null | undefined,
  base: PersonalHistory | null | undefined
): PersonalHistory | undefined =>
  edited || base
    ? {
        diet: edited?.diet ?? base?.diet ?? null,
        appetite: edited?.appetite ?? base?.appetite ?? null,
        sleep: edited?.sleep ?? base?.sleep ?? null,
        exercise: edited?.exercise ?? base?.exercise ?? null,
        bowel: edited?.bowel ?? base?.bowel ?? null,
        bladder: edited?.bladder ?? base?.bladder ?? null,
        addiction: edited?.addiction ?? base?.addiction ?? null,
        menstrual: edited?.menstrual ?? base?.menstrual ?? null,
      }
    : undefined;

export type CaseSheetEditableFieldKey =
  | 'patientSummary'
  | 'chiefComplaint'
  | 'clinicalSymptoms'
  | 'medicalHistory'
  | 'personalHistory'
  | 'familyHistory'
  | 'generalExamination'
  | 'investigations'
  | 'prescribedMedications'
  | 'treatmentPlan'
  | 'doctorNotes'
  | 'allergies'
  | 'socialHistory'
  | 'reviewOfSystems'
  | 'followUpPlan'
  | 'assessmentDiagnosis'
  | 'pediatricMilestone'
  | 'adtOrder'
  | 'medicationHistory'
  | 'dietAdvice'
  | 'doshaProfile'
  | 'diagnosisCodes'
  | 'diagnosisAyurvedic'
  | 'diagnosisAllopathic'
  | 'treatmentMedicines'
  | 'treatmentMedicinesTable'
  | 'treatmentPrinciple'
  | 'treatmentGiven'
  | 'dietNotes'
  | 'lifestyleNotes'
  | 'viruddhaAhaaraNotes';

type HistoryLikeItem = {
  relation?: unknown;
  condition?: unknown;
  diagnosis?: unknown;
  type?: unknown;
  value?: unknown;
  test?: unknown;
  unit?: unknown;
  notes?: unknown;
};

export type CaseSheetEditableFields = {
  patientSummary: string;
  chiefComplaint: string;
  clinicalSymptoms: string;
  medicalHistory: string;
  /** Structured personal history (Diet/Appetite/Sleep/Exercise/Bowel/Bladder/
   *  Addiction/Menstrual), editable as a single form — same convention as
   *  treatmentMedicinesTable: takes priority over the consultation prop
   *  whenever a doctor has edited it this session. */
  personalHistory?: PersonalHistory;
  familyHistory: string;
  generalExamination: string;
  investigations: string;
  prescribedMedications: SimplifiedMedication[];
  treatmentPlan: string;
  doctorNotes: string;
  allergies?: string;
  socialHistory?: string;
  reviewOfSystems?: string;
  followUpPlan?: string;
  assessmentDiagnosis?: string;
  pediatricMilestone?: string;
  adtOrder?: string;
  medicationHistory?: string;
  dietAdvice?: string;
  doshaProfile?: string;
  diagnosisCodes?: DiagnosisCodeItem[];
  // Diagnosis/Treatment are edited per sub-field (Ayurvedic/Allopathic,
  // Medicines/Treatment Principle/Treatment Given) — each gets its own
  // pencil + feedback buttons. Diet/Lifestyle/Viruddha Ahaara are edited
  // as one free-text blob per section.
  diagnosisAyurvedic?: string;
  diagnosisAllopathic?: string;
  treatmentMedicines?: string;
  /** Structured medicines table, editable as rows (Add/Remove) — takes
   *  priority over the plain-text treatmentMedicines above whenever it has
   *  entries, same convention as prescribedMedications/diagnosisCodes. */
  treatmentMedicinesTable?: PrescribedMedicine[];
  treatmentPrinciple?: string;
  treatmentGiven?: string;
  dietNotes?: string;
  lifestyleNotes?: string;
  viruddhaAhaaraNotes?: string;
};

export type CaseSheetViewProps = {
  // Header data
  header: {
    patientName: string;
    consultationDate: string;
    consultationTime: string;
    status: string;
    doctorName: string;
  };

  // Patient information
  patient: {
    name: string;
    age: number;
    gender: string;
    dateOfBirth: string;
    phoneNumber?: string | null;
    email?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    identifier: string;
  };

  // Consultation data
  consultation: {
    chiefComplaint?: string;
    vitals?: VitalSigns;
    analysis?: {
      historyOfPresentIllness?: string;
      reviewOfSystems?: string;
      physicalExamination?: string;
      assessment?: string;
      plan?: string;
    };
    aiAnalysis?: AIAnalysisResult;
    transcription?: TranscriptionSegment[];
    cdssSuggestions?: CDSSSuggestion[];
    status: ConsultationStatus;
    createdAt: string;
    completedAt?: string;
    uuid?: string;
    consultationId?: string;
    prescribedMedications?: SimplifiedMedication[];
    referenceSlokas?: ReferenceSlokaItem[];
    // Ayurveda-only, shown alongside Chief Complaint (matches the live
    // consultation page's Patient Information card).
    duration?: string;
    modeOfOnset?: string;
    // Ayurveda-only History/Personal History, matches the live consultation
    // page's Patient Information card.
    pastIllness?: string;
    treatmentHistory?: string;
    historyFamilyHistory?: string;
    currentMedication?: string;
    familySocialOccupational?: string;
    personalHistory?: PersonalHistory | null;
    // Ayurveda-only, matches the live consultation page's Diagnosis tiles.
    diagnosisAyurvedic?: string;
    diagnosisAllopathic?: string;
    // Ayurveda-only, matches the live consultation page's Treatment rows.
    treatmentMedicines?: string;
    /** Structured medicines table — rendered instead of the plain-text
     *  treatmentMedicines string when present. */
    treatmentMedicinesTable?: PrescribedMedicine[];
    treatmentPrinciple?: string;
    treatmentGiven?: string;
  };

  // Actions
  actions: {
    onBack?: () => void; // Optional - no back navigation if not provided
    onPrint: () => void;
    onExport: () => void;
    onCopy?: () => void;
    onSave?: () => void;
    isSaving?: boolean;
    isSaved?: boolean;
    onSaveDraft?: () => void;
    isDraftSaving?: boolean;
    /** Autosave to the DB, shown in the header so a failure is visible straight away. */
    autosaveStatus?: 'idle' | 'saving' | 'saved' | 'error';
    autosaveAt?: number;
    onRetryAutosave?: () => void;
    onSendToInvestigation?: () => void;
    isSendingToInvestigation?: boolean;
  };

  // Voice note
  voiceNote?: {
    state: VoiceNoteState;
    recordingDuration: number;
    onStart: () => void;
    onStop: () => void;
    errorMessage?: string | null;
  };

  // Audio recorder
  audioRecorder?: React.ReactNode;

  // Diet, Lifestyle, and Viruddha Ahaara recommendations
  diet?: string[];
  lifestyle?: string[];
  viruddhaAhaara?: string[];

  /** Whether the session is an Ayurveda consultation (shows Dosha Profile fields) */
  isAyurveda?: boolean;

  // Loading and error states
  loading?: boolean;
  error?: string | null;
  fields: CaseSheetEditableFields;
  onFieldChange?: <K extends CaseSheetEditableFieldKey>(
    field: K,
    value: CaseSheetEditableFields[K]
  ) => void;
  onVitalsChange?: (vitals: VitalSigns) => void;
  isSavingVitals?: boolean;
  onAddMedication?: (medication: SimplifiedMedication) => void;
  /**
   * Replace a medication with another medication
   * @param originalMedicationId - The ID of the medication to replace (used for robust matching)
   * @param originalMedicationName - The name of the medication to replace (fallback matching)
   * @param replacement - The replacement medication details
   */
  onReplaceMedication?: (
    originalMedicationId: string,
    originalMedicationName: string,
    replacement: SimplifiedMedication
  ) => void;
  /** Patient FHIR ID — used for lab report upload/fetch */
  patientId?: string;
};

export function CaseSheetView({
  patient,
  consultation,
  actions,
  voiceNote,
  audioRecorder,
  loading,
  error,
  fields,
  onFieldChange,
  onVitalsChange,
  isSavingVitals = false,
  onAddMedication,
  onReplaceMedication,
  patientId,
  diet,
  lifestyle,
  viruddhaAhaara,
  isAyurveda,
}: CaseSheetViewProps) {
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [expandedPrescriptionBlocks, setExpandedPrescriptionBlocks] = useState<Set<string>>(
    new Set(['medicines', 'investigations', 'diet', 'lifestyle', 'viruddha-ahaara'])
  );
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const user = useAuthStore((state) => state.user);
  const projectConfig = useAuthStore((state) => state.projectConfig);
  const prescriptionPageSize =
    projectConfig?.otherConfig?.prescriptionConfig?.prescriptionSize?.size === 'A5' ? 'A5' : 'A4';
  const { departmentKey, effectiveFieldOrder, fieldGroups, shouldShowadtOrder, departmentConfig } =
    useDepartmentConfig();
  // Get field-specific dropdown options from fieldOptions
  const getDropdownOptions = (fieldName: string): string[] | undefined => {
    const fieldOption = departmentConfig?.fieldOptions?.[fieldName];
    return fieldOption?.enabled ? fieldOption.options : undefined;
  };

  // Audio playback state
  const consultationId = consultation.consultationId || consultation.uuid;

  // Try to recover audio from IndexedDB if not in memory
  const { audio: recoveredAudio, isLoading: isRecoveringAudio } = useConsultationRecovery(
    consultationId || '',
    { autoRecover: true, populateAudioStore: true }
  );

  // Get audio from store (may be populated by recovery or from current session)
  const audioRecording = useAudioStore((state) =>
    consultationId ? state.getAudioRecording(consultationId) : undefined
  );

  // Use recovered audio or store audio
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const effectiveAudio =
    audioRecording ||
    (recoveredAudio
      ? {
          audioUrl: recoveredAudio.url,
          durationSeconds: recoveredAudio.durationSeconds,
          audioBlob: recoveredAudio.blob,
        }
      : null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element when recording is available
  useEffect(() => {
    const audioUrl = effectiveAudio?.audioUrl;
    if (!audioUrl) {
      return;
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    // Update current time during playback
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    // Handle playback end
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
      audioRef.current = null;
    };
  }, [effectiveAudio?.audioUrl]);

  // Toggle audio playback
  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch((err) => {
        logger.error('Failed to play audio:', err);
      });
      setIsPlaying(true);
    }
  }, [isPlaying]);

  // Calculate progress percentage
  const progressPercent = effectiveAudio?.durationSeconds
    ? (currentTime / effectiveAudio.durationSeconds) * 100
    : 0;

  // Seek to position when clicking on progress bar
  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !effectiveAudio?.durationSeconds) return;

      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * effectiveAudio.durationSeconds;

      audio.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [effectiveAudio?.durationSeconds]
  );

  // Seek audio to specific timestamp (for clicking on timestamp badges)
  const seekToTimestamp = useCallback(
    (timestampSeconds: number) => {
      const audio = audioRef.current;
      if (!audio || !effectiveAudio) {
        logger.warn('[CaseSheetView] Cannot seek: audio not available', {
          hasAudio: !!audio,
          hasRecording: !!effectiveAudio,
        });
        return;
      }

      audio.currentTime = timestampSeconds;
      setCurrentTime(timestampSeconds);

      // Start playing if not already playing
      if (!isPlaying) {
        audio
          .play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch((err) => {
            logger.error('[CaseSheetView] Failed to play audio:', err);
          });
      }
    },
    [effectiveAudio, isPlaying]
  );

  // Format timestamp as "00:10s" format
  const formatTimestampBadge = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}s`;
  };

  // Parse timestamp string like "0:03 - 0:08" or "1:22 - 1:23" to seconds
  // Takes the start time of the range
  const parseTimestampString = (timestamp: string | undefined | null): number | null => {
    if (!timestamp || typeof timestamp !== 'string') {
      return null;
    }

    // Handle range format: "0:03 - 0:08" -> take start time "0:03"
    const startTime = timestamp.split('-')[0].trim();

    // Parse "MM:SS" or "M:SS" format
    const parts = startTime.split(':');
    if (parts.length !== 2) {
      return null;
    }

    const mins = parseInt(parts[0], 10);
    const secs = parseInt(parts[1], 10);

    if (isNaN(mins) || isNaN(secs)) {
      return null;
    }

    const result = mins * 60 + secs;
    return result;
  };

  // Parse raw timestamp from various formats (number, string like "M:SS", numeric string)
  // Centralized helper to handle all timestamp parsing logic consistently
  const parseRawTimestamp = (rawTimestamp: unknown): number | null => {
    if (rawTimestamp === null || rawTimestamp === undefined || rawTimestamp === '') {
      return null;
    }

    // Handle numeric timestamp
    if (typeof rawTimestamp === 'number' && rawTimestamp > 0) {
      return rawTimestamp;
    }

    // Handle string timestamp (e.g., "0:03 - 0:08")
    if (typeof rawTimestamp === 'string') {
      const parsedTimestamp = parseTimestampString(rawTimestamp);
      if (parsedTimestamp !== null && parsedTimestamp > 0) {
        return parsedTimestamp;
      }
    }

    // Handle numeric string (e.g., "10.5")
    const numericTimestamp = parseFloat(String(rawTimestamp));
    if (!isNaN(numericTimestamp) && numericTimestamp > 0) {
      return numericTimestamp;
    }

    return null;
  };

  // Map editable field keys to AI analysis field keys
  // Note: Some fields use SessionSummary-style names (from LiveKit agent)
  // while others use AIAnalysisResult-style names
  const fieldToAnalysisMap: Record<string, string | null> = {
    patientSummary: 'patient_summary',
    chiefComplaint: 'chief_complaint',
    clinicalSymptoms: 'history_of_present_illness',
    medicalHistory: 'past_medical_surgical_history', // API returns past_medical_surgical_history
    familyHistory: 'family_history',
    generalExamination: 'examination',
    treatmentPlan: 'treatment_plan_management', // API returns treatment_plan_management
    doctorNotes: 'follow_up_plan', // API returns follow_up_plan for follow-up instructions
  };

  // Get timestamp for a field from AI analysis
  // Returns null if no real timestamp is available (badge will be hidden)
  const getFieldTimestamp = (field: string): number | null => {
    const analysisField = fieldToAnalysisMap[field];

    // Try to get real timestamp from backend
    if (analysisField && consultation.aiAnalysis) {
      // Use type assertion since API may return fields not in AIAnalysisResult type
      const fieldData = (consultation.aiAnalysis as unknown as Record<string, unknown>)[
        analysisField
      ];
      if (fieldData && typeof fieldData === 'object' && 'timestamp' in fieldData) {
        const rawTimestamp = (fieldData as { timestamp: unknown }).timestamp;

        const parsedTimestamp = parseRawTimestamp(rawTimestamp);
        if (parsedTimestamp !== null) {
          return parsedTimestamp;
        }
      }
    }

    // No real timestamp — return null so badge is hidden
    return null;
  };

  // Extract symptom codes from history_of_present_illness for Diagnosis table
  const getSymptomCodes = (): DiagnosisCodeItem[] => {
    const fieldData = consultation.aiAnalysis?.history_of_present_illness;
    if (!fieldData || typeof fieldData !== 'object') return [];
    const raw = (fieldData as unknown as Record<string, unknown>).symptom_codes;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item: unknown) => {
        if (!item || typeof item !== 'object') return null;
        const obj = item as Record<string, unknown>;
        const symptom = typeof obj.symptom === 'string' ? obj.symptom : '';
        const symptomId = typeof obj.symptom_id === 'string' ? obj.symptom_id : '';
        const parts = symptomId.split('|');
        // "SYSTEM|CODE|DESCRIPTION" (e.g. "ICD-10|A78|Q fever") when the system is known;
        // falls back to treating the whole string as the code for older/unqualified data.
        const hasSystem = parts.length > 1;
        const type = hasSystem ? parts[0] : 'ICD-11';
        const code = hasSystem ? parts[1] : symptomId;
        const description = (hasSystem ? parts.slice(2).join('|') : '') || '';
        return symptom ? { symptom, code, description, type } : null;
      })
      .filter(Boolean) as DiagnosisCodeItem[];
  };

  // Handler function for toggling prescription block expand/collapse state
  const handlePrescriptionBlockToggle =
    (blockName: 'medicines' | 'investigations' | 'diet' | 'lifestyle' | 'viruddha-ahaara') =>
    (open: boolean) => {
      setExpandedPrescriptionBlocks((prev) => {
        const next = new Set(prev);
        if (open) {
          next.add(blockName);
        } else {
          next.delete(blockName);
        }
        return next;
      });
    };

  type AnalysisFieldLike = AIAnalysisField | string | string[] | undefined | null;

  const getAnalysisFieldValue = (field: AnalysisFieldLike): string | undefined => {
    if (!field) {
      return undefined;
    }

    // Handle string directly
    if (typeof field === 'string') {
      return field.trim() || undefined;
    }

    // Handle array directly
    if (Array.isArray(field)) {
      if (field.length > 0 && (field as unknown[]).every(isRelationConditionItem)) {
        return (
          formatRelationConditionList(field as unknown as RelationConditionEntry[]) || undefined
        );
      }
      return (
        field
          .map((item) => {
            if (typeof item === 'string') return item;
            if (typeof item === 'object' && item !== null) {
              const obj = item as HistoryLikeItem;
              // Handle family history objects (mixed-array fallback — the
              // common all-relation-condition case is short-circuited above)
              if ('relation' in obj && 'condition' in obj) {
                return formatRelationConditionEntry(obj);
              }
              // Handle investigation/lab-result objects, e.g. { test, value, unit, notes }
              if ('test' in obj) {
                return formatInvestigationEntry(obj);
              }
              // Handle diagnosis objects
              if ('diagnosis' in obj) {
                return formatDiagnosisEntry(obj as { diagnosis: unknown; notes?: unknown });
              }
              // Handle examination objects
              if ('type' in obj && 'value' in obj) {
                return `${obj.type}: ${obj.value}`;
              }
            }
            return '';
          })
          .filter((item) => item.trim())
          .join(', ') || undefined
      );
    }

    // Handle AIAnalysisField object
    if (typeof field === 'object' && field !== null && 'value' in field) {
      const value = (field as AIAnalysisField).value;

      if (Array.isArray(value)) {
        if (value.length > 0 && (value as unknown[]).every(isRelationConditionItem)) {
          return (
            formatRelationConditionList(value as unknown as RelationConditionEntry[]) || undefined
          );
        }
        return (
          value
            .map((item) => {
              if (typeof item === 'string') return item;
              if (typeof item === 'object' && item !== null) {
                const obj = item as HistoryLikeItem;
                // Handle family history objects (mixed-array fallback — the
                // common all-relation-condition case is short-circuited above)
                if ('relation' in obj && 'condition' in obj) {
                  return formatRelationConditionEntry(obj);
                }
                // Handle diagnosis objects
                if ('diagnosis' in obj) {
                  return String((obj as { diagnosis?: unknown }).diagnosis ?? '').trim();
                }
                // Handle examination objects
                if ('type' in obj && 'value' in obj) {
                  return `${obj.type}: ${obj.value}`;
                }
              }
              return '';
            })
            .filter((item) => item.trim())
            .join(', ') || undefined
        );
      }

      if (typeof value === 'string') {
        return value.trim() || undefined;
      }

      // If value is an object, return undefined to avoid [object Object]
      return undefined;
    }

    // For any other object type, return undefined to prevent [object Object]
    return undefined;
  };

  // Helper function to extract investigations_orders, handling both simple and nested structures
  const extractInvestigationsOrders = (
    investigationsOrders?: AIAnalysisField | { [key: string]: unknown } | undefined
  ): string => {
    if (!investigationsOrders) {
      return '';
    }

    // Handle simple AIAnalysisField structure
    if (typeof investigationsOrders === 'object' && 'value' in investigationsOrders) {
      const value = getAnalysisFieldValue(investigationsOrders as AIAnalysisField);
      return value || '';
    }

    // Handle nested object structure (e.g., { laboratory_tests: {...}, imaging_studies: {...} })
    if (typeof investigationsOrders === 'object' && investigationsOrders !== null) {
      const parts: string[] = [];

      // Extract laboratory_tests
      if ('laboratory_tests' in investigationsOrders) {
        const labTests = getAnalysisFieldValue(
          investigationsOrders.laboratory_tests as AIAnalysisField
        );
        if (labTests) {
          parts.push(`Laboratory Tests: ${labTests}`);
        }
      }

      // Extract imaging_studies
      if ('imaging_studies' in investigationsOrders) {
        const imaging = getAnalysisFieldValue(
          investigationsOrders.imaging_studies as AIAnalysisField
        );
        if (imaging) {
          parts.push(`Imaging Studies: ${imaging}`);
        }
      }

      // Extract other_procedures
      if ('other_procedures' in investigationsOrders) {
        const procedures = getAnalysisFieldValue(
          investigationsOrders.other_procedures as AIAnalysisField
        );
        if (procedures) {
          parts.push(`Other Procedures: ${procedures}`);
        }
      }

      return parts.join('. ');
    }

    return '';
  };

  // Simple function to extract family history - just use the direct value
  const extractFamilyHistory = (analysis: AIAnalysisResult): string => {
    return getAnalysisFieldValue(analysis.family_history) || '';
  };

  // Simple function to extract medical history - combine direct fields only
  const extractMedicalHistory = (analysis: AIAnalysisResult): string => {
    const parts: string[] = [];

    const directMedicalHistory = getAnalysisFieldValue(analysis.medical_history);
    if (directMedicalHistory) {
      parts.push(directMedicalHistory);
    }

    const coMorbidities = getAnalysisFieldValue(analysis.co_morbidity);
    if (coMorbidities) {
      parts.push(coMorbidities);
    }

    return parts.join('. ');
  };

  const medicalHistoryText = consultation.aiAnalysis
    ? extractMedicalHistory(consultation.aiAnalysis)
    : '';
  const familyHistoryText = consultation.aiAnalysis
    ? extractFamilyHistory(consultation.aiAnalysis)
    : '';

  const fallbackMedications = consultation.prescribedMedications || [];

  const displayMedications =
    fields.prescribedMedications && fields.prescribedMedications.length > 0
      ? fields.prescribedMedications
      : fallbackMedications;

  // Ayurveda Medicines table (name/form/dose/anupana/timing/frequency/duration/
  // additional_info) is a separate structure from the generic prescribedMedications
  // list above. Prescription printing/WhatsApp-send only understood the generic
  // list, so Ayurveda medicines entered only in the Medicines table never showed
  // up on the printed prescription. Convert and append them here so both paths
  // include everything, same as the Case Sheet PDF and Patient Record already do.
  const displayTreatmentMedicinesTable: PrescribedMedicine[] =
    fields.treatmentMedicinesTable && fields.treatmentMedicinesTable.length > 0
      ? fields.treatmentMedicinesTable
      : consultation.treatmentMedicinesTable || [];

  // Rows the right-hand Prescription panel and Print Prescription show for
  // Ayurveda — unnamed rows are blank placeholders from the editor.
  const ayurvedaPrescriptionMedicines = displayTreatmentMedicinesTable.filter((med) =>
    med.name?.trim()
  );

  const treatmentMedicinesAsStructured: StructuredMedication[] = displayTreatmentMedicinesTable
    .filter((med) => med.name?.trim())
    .map((med) => ({
      medication: med.name || '',
      dosage: med.dose || '',
      frequency: med.frequency || '',
      timing: med.timing ? [med.timing] : [],
      duration: med.duration || '',
      medicineType: med.form || undefined,
      relationWithMeal: med.timing || undefined,
      // Only used by the server-generated (WhatsApp) prescription, which takes
      // StructuredMedication. The on-screen Print Prescription passes the
      // Medicines table through as `ayurvedaMedicines` instead, so Anupana /
      // Timing / Additional Info keep their own columns.
      additionalInfo: med.anupana || undefined,
    }));

  // When the Ayurveda Medicines table has entries, it's the authoritative,
  // fully-structured source — use it exclusively for the prescription so a
  // stray/legacy plain-text entry in the generic medications list (e.g. an
  // AI-generated summary sentence saved as a single "medication" string)
  // doesn't also print as a garbled, unstructured row alongside it.
  const displayMedicationsForPrescription: StructuredMedication[] =
    treatmentMedicinesAsStructured.length > 0 ? treatmentMedicinesAsStructured : displayMedications;

  // Check if there's any actual CDSS data to display (non-empty arrays)
  const pranikCdss = consultation.aiAnalysis?.pranik_cdss;
  const hasBetterMedSuggestions =
    pranikCdss?.betterMedicationSuggestions?.value &&
    Array.isArray(pranikCdss.betterMedicationSuggestions.value) &&
    pranikCdss.betterMedicationSuggestions.value.length > 0;
  const hasDrugInteractionAlerts =
    pranikCdss?.drugInteractionAlerts?.value &&
    Array.isArray(pranikCdss.drugInteractionAlerts.value) &&
    pranikCdss.drugInteractionAlerts.value.length > 0;
  const hasMissedSymptomAlerts =
    pranikCdss?.missedSymptomAlerts?.value &&
    Array.isArray(pranikCdss.missedSymptomAlerts.value) &&
    pranikCdss.missedSymptomAlerts.value.length > 0;
  const hasAllergyConflictAlerts =
    pranikCdss?.allergyConflictAlerts?.value &&
    Array.isArray(pranikCdss.allergyConflictAlerts.value) &&
    pranikCdss.allergyConflictAlerts.value.length > 0;

  // Only show CDSS section if there's actual data to display
  const hasCdssData =
    hasBetterMedSuggestions ||
    hasDrugInteractionAlerts ||
    hasMissedSymptomAlerts ||
    hasAllergyConflictAlerts;

  const diagnosisText =
    fields.assessmentDiagnosis ||
    (consultation.aiAnalysis ? getAnalysisFieldValue(consultation.aiAnalysis.diagnosis) : '');

  type TextFieldKey = Exclude<
    CaseSheetEditableFieldKey,
    'prescribedMedications' | 'diagnosisCodes' | 'treatmentMedicinesTable' | 'personalHistory'
  >;

  const fieldLabels: Record<string, string> = {
    patientSummary: 'Patient Summary',
    chiefComplaint: 'Chief Complaint',
    chiefComplaints: 'Chief Complaints',
    clinicalSymptoms: 'Relevant Findings',
    medicalHistory: 'Medical History',
    personalHistory: 'Personal History',
    familyHistory: 'Family History',
    generalExamination: 'General Examination',
    investigations: 'Investigations',
    prescribedMedications: 'Medication',
    medications: 'Medication',
    treatmentPlan: 'Treatment Plan',
    doctorNotes: "Doctor's Notes",
    allergies: 'Allergies',
    socialHistory: 'Social History',
    reviewOfSystems: 'Review of Systems',
    followUpPlan: 'Follow Up Plan',
    assessmentDiagnosis: 'Assessment & Diagnosis',
    diagnosisCodes: 'Diagnosis',
    vitals: 'Vitals',
    pediatricMilestone: 'Pediatric Milestones',
    adtOrder: 'ADT Orders',
    medicationHistory: 'Drug History',
    dietAdvice: 'Diet Advice',
    // Grouped section labels
    otherHistory: 'Other History',
    historyOther: 'History - Others',
    pastHistory: 'Past History',
    examination: 'Examination',
    assessment: 'Assessment',
    diagnosis: 'Diagnosis',
    patientEducation: 'Patient Education',
    adviceFollowUp: 'Advice & Follow Up',
    doshaProfile: 'Astha Sthaana Pareeksha',
    diagnosisAyurvedic: 'Ayurvedic',
    diagnosisAllopathic: 'Allopathic',
    treatmentMedicines: 'Medicines',
    treatmentMedicinesTable: 'Medicines',
    treatmentPrinciple: 'Treatment Principle',
    treatmentGiven: 'Treatment Given',
    dietNotes: 'Diet',
    lifestyleNotes: 'Lifestyle',
    viruddhaAhaaraNotes: 'Viruddha Ahaara',
  };

  const departmentFieldLabels: Partial<Record<CaseSheetEditableFieldKey, string>> =
    departmentKey === 'pediatrics'
      ? {
          treatmentPlan: 'Patient Education',
          followUpPlan: 'Advice and Follow up',
          assessmentDiagnosis: 'Diagnosis',
        }
      : departmentKey === 'orthopedic'
        ? {
            assessmentDiagnosis: 'Diagnosis',
          }
        : departmentKey === 'cardiology'
          ? {
              treatmentPlan: 'Patient Education',
              assessmentDiagnosis: 'Diagnosis',
            }
          : departmentKey === 'general_surgery'
            ? {
                assessmentDiagnosis: 'Diagnosis',
              }
            : {};

  const getFieldLabel = (field: CaseSheetEditableFieldKey | string, section?: string): string => {
    if (departmentKey === 'cardiology' && field === 'treatmentPlan' && section === 'examination') {
      return 'Treatment Plan';
    }

    return departmentFieldLabels[field as CaseSheetEditableFieldKey] || fieldLabels[field] || field;
  };

  const emptyMessages: Record<TextFieldKey, string> = {
    patientSummary: 'No patient summary recorded',
    chiefComplaint: 'No chief complaint recorded',
    clinicalSymptoms: 'No clinical symptoms recorded',
    medicalHistory: 'No medical history recorded',
    familyHistory: 'No family history recorded',
    generalExamination: 'No examination findings recorded',
    investigations: 'No investigations recorded',
    treatmentPlan: 'No treatment plan recorded',
    doctorNotes: 'Add additional notes if any',
    allergies: 'No known allergies',
    socialHistory: 'No social history recorded',
    reviewOfSystems: 'No review of systems recorded',
    followUpPlan: 'No follow up plan recorded',
    assessmentDiagnosis: 'No assessment or diagnosis recorded',
    pediatricMilestone: 'No pediatric milestones recorded',
    adtOrder: 'No ADT orders recorded',
    medicationHistory: 'No drug history recorded',
    dietAdvice: 'No diet advice recorded',
    doshaProfile: 'Dosha profile not recorded',
    diagnosisAyurvedic: 'No ayurvedic diagnosis recorded',
    diagnosisAllopathic: 'No allopathic diagnosis recorded',
    treatmentMedicines: 'No medicines recorded',
    treatmentPrinciple: 'No treatment principle recorded',
    treatmentGiven: 'No treatment given recorded',
    dietNotes: 'No diet notes recorded',
    lifestyleNotes: 'No lifestyle notes recorded',
    viruddhaAhaaraNotes: 'No viruddha ahaara notes recorded',
  };

  const departmentEmptyMessages: Partial<Record<TextFieldKey, string>> =
    departmentKey === 'pediatrics'
      ? {
          treatmentPlan: 'No patient education recorded',
          followUpPlan: 'No advice and follow up recorded',
          assessmentDiagnosis: 'No diagnosis recorded',
        }
      : departmentKey === 'orthopedic'
        ? {
            assessmentDiagnosis: 'No diagnosis recorded',
          }
        : departmentKey === 'cardiology'
          ? {
              treatmentPlan: 'No patient education recorded',
              assessmentDiagnosis: 'No diagnosis recorded',
              clinicalSymptoms: 'No relevant findings recorded',
            }
          : departmentKey === 'general_surgery'
            ? {
                assessmentDiagnosis: 'No diagnosis recorded',
              }
            : {};

  const getFieldEmptyMessage = (field: TextFieldKey, section?: string): string =>
    departmentKey === 'cardiology' && field === 'treatmentPlan' && section === 'examination'
      ? emptyMessages.treatmentPlan
      : departmentEmptyMessages[field] || emptyMessages[field];

  const renderAyurvedaRecommendationBlock = (
    blockName: 'diet' | 'lifestyle' | 'viruddha-ahaara'
  ) => {
    const blockConfig =
      blockName === 'diet'
        ? {
            title: 'Diet',
            emptyMessage: 'No diet recommendations',
            items: diet,
            field: 'dietNotes' as const,
          }
        : blockName === 'lifestyle'
          ? {
              title: 'Lifestyle',
              emptyMessage: 'No lifestyle recommendations',
              items: lifestyle,
              field: 'lifestyleNotes' as const,
            }
          : {
              title: 'Viruddha Ahaara',
              emptyMessage: 'No viruddha ahaara recorded',
              items: viruddhaAhaara,
              field: 'viruddhaAhaaraNotes' as const,
            };

    const hasItems = blockConfig.items && blockConfig.items.length > 0;
    const isEditingThis = editingField === blockConfig.field;

    return (
      <AccordionItem key={blockName} value={blockName} className="!border-none !pb-0">
        <AccordionTrigger className={summarizerTriggerClassName}>
          <div className="flex items-center gap-2 text-left">
            <span className={summarizerHeadingClassName}>{blockConfig.title}</span>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <div onClick={(e) => e.stopPropagation()}>
                {renderHeaderActions(blockConfig.field)}
              </div>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
          </div>
        </AccordionTrigger>
        <AccordionContent className={summarizerContentClassName}>
          {isEditingThis ? (
            <div className={summarizerFieldContainerClassName}>
              <Textarea
                value={draftText}
                onChange={(_, value) => setDraftText(value)}
                autoResize
                minRows={3}
                placeholder={`Enter ${blockConfig.title.toLowerCase()} notes`}
              />
            </div>
          ) : (
            <>
              <div className={summarizerFieldContainerClassName}>
                {hasItems ? (
                  <ul className="list-disc space-y-1 pl-5">
                    {blockConfig.items!.map((item, idx) => (
                      <li key={`${blockName}-${idx}`} className={summarizerBodyClassName}>
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={summarizerBodyClassName}>{blockConfig.emptyMessage}</p>
                )}
              </div>
              {renderFeedbackButtons()}
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    );
  };

  // Diet/Lifestyle rendered as rows nested inside the Treatment section
  // (after Treatment Given) rather than their own top-level accordion items —
  // same row style (label + pencil header, box/list, feedback) as the
  // Medicines/Treatment Principle/Treatment Given rows above them. Viruddha
  // Ahaara stays a separate top-level section via renderAyurvedaRecommendationBlock.
  // A doctor's edit (fields.dietNotes/lifestyleNotes — one item per line)
  // takes priority over the AI-suggested list whenever it has content, same
  // convention as treatmentMedicinesTable/personalHistory above.
  const linesToItems = (text?: string): string[] =>
    (text ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  const renderAyurvedaRecommendationRow = (blockName: 'diet' | 'lifestyle') => {
    const blockConfig =
      blockName === 'diet'
        ? {
            title: 'Diet',
            emptyMessage: 'No diet recommendations',
            items: fields.dietNotes?.trim() ? linesToItems(fields.dietNotes) : diet,
            field: 'dietNotes' as const,
          }
        : {
            title: 'Lifestyle',
            emptyMessage: 'No lifestyle recommendations',
            items: fields.lifestyleNotes?.trim() ? linesToItems(fields.lifestyleNotes) : lifestyle,
            field: 'lifestyleNotes' as const,
          };

    const hasItems = blockConfig.items && blockConfig.items.length > 0;
    const isEditingThis = editingField === blockConfig.field;

    return (
      <div key={blockName}>
        <div className="mb-2 flex min-h-[28px] items-center justify-between gap-2">
          <span className="font-hanken text-[16px] font-medium leading-[20px] tracking-[-0.16px] text-[#051858]">
            {blockConfig.title}
          </span>
          {canEdit && (
            <div className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
              {renderHeaderActions(blockConfig.field)}
            </div>
          )}
        </div>
        {isEditingThis ? (
          <div className={summarizerFieldContainerClassName}>
            <Textarea
              value={draftText}
              onChange={(_, value) => setDraftText(value)}
              autoResize
              minRows={3}
              placeholder={`Enter ${blockConfig.title.toLowerCase()} notes`}
            />
          </div>
        ) : (
          <>
            <div className={summarizerFieldContainerClassName}>
              {hasItems ? (
                <ul className="list-disc space-y-1 pl-5">
                  {blockConfig.items!.map((item, idx) => (
                    <li key={`${blockName}-${idx}`} className={summarizerBodyClassName}>
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={summarizerBodyClassName}>{blockConfig.emptyMessage}</p>
              )}
            </div>
            {renderFeedbackButtons()}
          </>
        )}
      </div>
    );
  };

  const aiPatientSummary = getAnalysisFieldValue(consultation.aiAnalysis?.patient_summary);
  const aiChiefComplaint = getAnalysisFieldValue(consultation.aiAnalysis?.chief_complaint);
  const aiClinicalSymptoms = getAnalysisFieldValue(
    consultation.aiAnalysis?.history_of_present_illness
  );
  const aiGeneralExamination = getAnalysisFieldValue(consultation.aiAnalysis?.examination);
  const aiTreatmentPlan = getAnalysisFieldValue(consultation.aiAnalysis?.follow_up_details);
  const aiDoctorNotes = getAnalysisFieldValue(consultation.aiAnalysis?.doctor_notes);

  // Extract symptom codes for Diagnosis table
  const symptomCodes = getSymptomCodes();
  const displayDiagnosisCodes: DiagnosisCodeItem[] =
    fields.diagnosisCodes && fields.diagnosisCodes.length > 0
      ? fields.diagnosisCodes
      : symptomCodes;

  const fallbackTextValues: Record<TextFieldKey, string | undefined> = {
    patientSummary: aiPatientSummary || undefined,
    chiefComplaint: aiChiefComplaint || undefined,
    clinicalSymptoms: aiClinicalSymptoms || undefined,
    medicalHistory: medicalHistoryText || undefined,
    familyHistory: familyHistoryText || undefined,
    generalExamination: aiGeneralExamination || undefined,
    investigations: undefined, // seeded into fields.investigations from CaseSheetPage
    treatmentPlan: aiTreatmentPlan || undefined,
    doctorNotes: aiDoctorNotes || undefined,
    assessmentDiagnosis: diagnosisText || undefined,
    allergies: undefined,
    socialHistory: undefined,
    reviewOfSystems: undefined,
    followUpPlan: undefined,
    pediatricMilestone: undefined,
    adtOrder: undefined,
    medicationHistory: undefined,
    dietAdvice: undefined,
    doshaProfile: undefined,
    diagnosisAyurvedic: consultation.diagnosisAyurvedic || undefined,
    diagnosisAllopathic: consultation.diagnosisAllopathic || undefined,
    treatmentMedicines: consultation.treatmentMedicines || undefined,
    treatmentPrinciple: consultation.treatmentPrinciple || undefined,
    treatmentGiven: consultation.treatmentGiven || undefined,
    // Pre-fill the editor with the AI-suggested list (one item per line) so
    // editing starts from what's actually shown, not blank.
    dietNotes: diet && diet.length > 0 ? diet.join('\n') : undefined,
    lifestyleNotes: lifestyle && lifestyle.length > 0 ? lifestyle.join('\n') : undefined,
    viruddhaAhaaraNotes: undefined,
  };

  const getDisplayText = (field: TextFieldKey, section?: string): string => {
    const storedValue = fields[field];
    if (storedValue && storedValue.trim().length > 0) {
      return storedValue;
    }
    const fallback = fallbackTextValues[field];
    if (fallback && fallback.trim().length > 0) {
      return fallback;
    }
    return getFieldEmptyMessage(field, section);
  };

  // Backend sends ADT order as a raw snake_case enum value (e.g. "out_patient") — humanize it for display.
  const formatAdtOrderText = (text: string): string =>
    text
      .replace(/[_-]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const canEdit = Boolean(onFieldChange);
  const canEditVitals = Boolean(onVitalsChange);
  const [editingField, setEditingField] = useState<CaseSheetEditableFieldKey | null>(null);
  const [isEditingVitals, setIsEditingVitals] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [draftMedications, setDraftMedications] = useState<SimplifiedMedication[]>([]);
  const [draftDiagnosisCodes, setDraftDiagnosisCodes] = useState<DiagnosisCodeItem[]>([]);
  const [draftTreatmentMedicinesTable, setDraftTreatmentMedicinesTable] = useState<
    PrescribedMedicine[]
  >([]);
  const [draftPersonalHistory, setDraftPersonalHistory] =
    useState<PersonalHistory>(EMPTY_PERSONAL_HISTORY);
  const isAnySectionEditing = Boolean(editingField) || isEditingVitals;

  const isTextField = (
    field: CaseSheetEditableFieldKey
  ): field is Exclude<
    CaseSheetEditableFieldKey,
    'prescribedMedications' | 'diagnosisCodes' | 'treatmentMedicinesTable' | 'personalHistory'
  > =>
    field !== 'prescribedMedications' &&
    field !== 'diagnosisCodes' &&
    field !== 'treatmentMedicinesTable' &&
    field !== 'personalHistory';

  const beginEdit = (field: CaseSheetEditableFieldKey) => {
    if (editingField === field) {
      return;
    }

    setIsEditingVitals(false);

    if (isTextField(field)) {
      const fallback = fallbackTextValues[field];
      const initialValue =
        fields[field] && fields[field].trim().length > 0
          ? fields[field]
          : fallback && fallback.trim().length > 0
            ? fallback
            : '';
      setDraftText(initialValue);
    } else if (field === 'diagnosisCodes') {
      const normalizedSource =
        displayDiagnosisCodes.length > 0
          ? displayDiagnosisCodes.map((item) => ({ ...item }))
          : [{ symptom: '', code: '', description: '' }];

      setDraftDiagnosisCodes(normalizedSource);
    } else if (field === 'treatmentMedicinesTable') {
      const source =
        fields.treatmentMedicinesTable && fields.treatmentMedicinesTable.length > 0
          ? fields.treatmentMedicinesTable
          : (consultation.treatmentMedicinesTable ?? []);

      const normalizedSource =
        source.length > 0
          ? source.map((med) => ({ ...med }))
          : [
              {
                name: '',
                form: null,
                dose: null,
                anupana: null,
                timing: null,
                frequency: null,
                duration: null,
                additional_info: null,
              },
            ];

      setDraftTreatmentMedicinesTable(normalizedSource);
    } else if (field === 'personalHistory') {
      const source = mergePersonalHistory(fields.personalHistory, consultation.personalHistory);
      setDraftPersonalHistory(
        source ? { ...EMPTY_PERSONAL_HISTORY, ...source } : EMPTY_PERSONAL_HISTORY
      );
    } else {
      const source =
        fields.prescribedMedications && fields.prescribedMedications.length > 0
          ? fields.prescribedMedications
          : fallbackMedications;

      const normalizedSource =
        source.length > 0
          ? source.map((med) => ({ ...med, timing: [...med.timing] }))
          : [{ medication: '', dosage: '', frequency: '', timing: [] as string[], duration: '' }];

      setDraftMedications(normalizedSource);
    }

    setEditingField(field);
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setDraftText('');
    setDraftMedications([]);
    setDraftDiagnosisCodes([]);
    setDraftTreatmentMedicinesTable([]);
    setDraftPersonalHistory(EMPTY_PERSONAL_HISTORY);
  };

  const handleSaveEdit = () => {
    if (!editingField || !onFieldChange) {
      handleCancelEdit();
      return;
    }

    if (isTextField(editingField)) {
      onFieldChange(editingField, draftText.trim());
    } else if (editingField === 'diagnosisCodes') {
      const cleaned = draftDiagnosisCodes
        .filter((item) => item.symptom.trim().length > 0)
        .map((item) => ({
          symptom: item.symptom.trim(),
          code: item.code.trim(),
          description: item.description.trim(),
          type: (item.type || 'ICD-11').trim(),
        }));
      onFieldChange('diagnosisCodes', cleaned);
    } else if (editingField === 'treatmentMedicinesTable') {
      const cleaned = draftTreatmentMedicinesTable
        .filter((med) => med.name && med.name.trim().length > 0)
        .map((med) => ({
          ...med,
          name: (med.name ?? '').trim(),
        }));
      onFieldChange('treatmentMedicinesTable', cleaned);
    } else if (editingField === 'personalHistory') {
      const trimOrNull = (v: string | null) => {
        const trimmed = (v ?? '').trim();
        return trimmed.length > 0 ? trimmed : null;
      };
      onFieldChange('personalHistory', {
        diet: trimOrNull(draftPersonalHistory.diet),
        appetite: trimOrNull(draftPersonalHistory.appetite),
        sleep: trimOrNull(draftPersonalHistory.sleep),
        exercise: trimOrNull(draftPersonalHistory.exercise),
        bowel: trimOrNull(draftPersonalHistory.bowel),
        bladder: trimOrNull(draftPersonalHistory.bladder),
        addiction: trimOrNull(draftPersonalHistory.addiction),
        menstrual: trimOrNull(draftPersonalHistory.menstrual),
      });
    } else {
      // Trim each medication's timing entries on save (user may have typed "a, b, ")
      const cleaned = draftMedications
        .filter((med) => med.medication.trim().length > 0)
        .map((med) => ({
          ...med,
          timing: med.timing.map((t) => t.trim()).filter(Boolean),
        }));
      onFieldChange('prescribedMedications', cleaned);
    }

    handleCancelEdit();
  };

  const handleDiagnosisCodeFieldChange = (
    index: number,
    field: keyof DiagnosisCodeItem,
    value: string
  ) => {
    setDraftDiagnosisCodes((prev) => {
      const next = prev.map((item) => ({ ...item }));
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleDiagnosisCodeRemove = (index: number) => {
    setDraftDiagnosisCodes((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleDiagnosisCodeAdd = () => {
    setDraftDiagnosisCodes((prev) => [
      ...prev,
      { symptom: '', code: '', description: '', type: 'ICD-11' },
    ]);
  };

  const handleMedicationFieldChange = (
    index: number,
    field: keyof SimplifiedMedication,
    value: string
  ) => {
    setDraftMedications((prev) => {
      const next = prev.map((med) => ({ ...med, timing: [...med.timing] }));
      if (field === 'timing') {
        // Don't filter(Boolean) on keystroke — that strips trailing commas/spaces while typing.
        // Empty entries are cleaned when the user confirms the edit in handleSaveEdit.
        next[index] = { ...next[index], timing: value.split(',').map((t) => t.trim()) };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const handleMedicationRemove = (index: number) => {
    setDraftMedications((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleMedicationAdd = () => {
    setDraftMedications((prev) => [
      ...prev,
      { medication: '', dosage: '', frequency: '', timing: [], duration: '' },
    ]);
  };

  const handleTreatmentMedicineFieldChange = (
    index: number,
    field: keyof PrescribedMedicine,
    value: string
  ) => {
    setDraftTreatmentMedicinesTable((prev) => {
      const next = prev.map((med) => ({ ...med }));
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleTreatmentMedicineRemove = (index: number) => {
    setDraftTreatmentMedicinesTable((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleTreatmentMedicineAdd = () => {
    setDraftTreatmentMedicinesTable((prev) => [
      ...prev,
      {
        name: '',
        form: null,
        dose: null,
        anupana: null,
        timing: null,
        frequency: null,
        duration: null,
        additional_info: null,
      },
    ]);
  };

  const renderHeaderActions = (field: CaseSheetEditableFieldKey) => {
    if (!canEdit) {
      return null;
    }

    if (editingField === field) {
      return (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Save ${getFieldLabel(field)}`}
            onClick={handleSaveEdit}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Cancel editing ${getFieldLabel(field)}`}
            onClick={handleCancelEdit}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Edit ${getFieldLabel(field)}`}
        onClick={() => beginEdit(field)}
        disabled={isAnySectionEditing && editingField !== field}
        className="bg-transparent hover:bg-gray-100 active:bg-gray-200"
      >
        {/* text-primary follows the department theme: blue (default/General Medicine)
            or green (.theme-ayurveda). Do NOT hardcode a color here. */}
        <Pencil className="h-4 w-4 text-primary" />
      </Button>
    );
  };

  const renderFeedbackButtons = () => (
    <div className="mt-2 flex items-center gap-2 px-1">
      <button
        type="button"
        aria-label="Thumbs up"
        title="Thumbs up"
        className="flex items-center justify-center rounded p-1 text-[#969AAC] hover:bg-gray-100"
      >
        <ThumbUp size={14} fill="#969AAC" />
      </button>
      <button
        type="button"
        aria-label="Thumbs down"
        title="Thumbs down"
        className="flex items-center justify-center rounded p-1 text-[#969AAC] hover:bg-gray-100"
      >
        <ThumbDown size={14} fill="#969AAC" />
      </button>
    </div>
  );

  const renderVitalsHeaderActions = () => {
    if (!canEditVitals) {
      return null;
    }

    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Edit Vitals"
        onClick={() => {
          setEditingField(null);
          setDraftText('');
          setDraftMedications([]);
          setIsEditingVitals(true);
        }}
        disabled={isAnySectionEditing && !isEditingVitals}
      >
        {/* text-primary follows the department theme (blue default / green Ayurveda). */}
        <Pencil className="h-4 w-4 text-primary" />
      </Button>
    );
  };

  const renderMedicationEditor = () => (
    <div className="space-y-4">
      {draftMedications.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No medications listed. Add a medication using the button below.
        </p>
      ) : (
        draftMedications.map((med, index) => (
          <div key={`med-edit-${index}`} className="space-y-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <span className="w-6 text-xs font-medium text-gray-500">{index + 1}.</span>
              <Input
                value={med.medication}
                onChange={(event) =>
                  handleMedicationFieldChange(index, 'medication', event.target.value)
                }
                placeholder="Medication name"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove medication ${index + 1}`}
                onClick={() => handleMedicationRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-4 gap-2 pl-8">
              <Input
                value={med.dosage}
                onChange={(event) =>
                  handleMedicationFieldChange(index, 'dosage', event.target.value)
                }
                placeholder="Dosage"
                className="text-sm"
              />
              <Input
                value={med.frequency}
                onChange={(event) =>
                  handleMedicationFieldChange(index, 'frequency', event.target.value)
                }
                placeholder="Frequency"
                className="text-sm"
              />
              <Input
                value={med.duration}
                onChange={(event) =>
                  handleMedicationFieldChange(index, 'duration', event.target.value)
                }
                placeholder="Duration"
                className="text-sm"
              />
              <Input
                value={med.timing.join(', ')}
                onChange={(event) =>
                  handleMedicationFieldChange(index, 'timing', event.target.value)
                }
                placeholder="Timing (e.g. Morning, Evening)"
                className="text-sm"
              />
            </div>
          </div>
        ))
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleMedicationAdd}
        leftIcon={<Plus className="h-4 w-4" />}
      >
        Add medication
      </Button>
    </div>
  );

  // Editor for the Ayurveda Treatment > Medicines structured table — same
  // add/remove-row pattern as renderMedicationEditor above, with the
  // PrescribedMedicine schema's 8 fields instead of StructuredMedication's.
  const renderTreatmentMedicinesTableEditor = () => (
    <div className="space-y-4">
      {draftTreatmentMedicinesTable.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No medicines listed. Add one using the button below.
        </p>
      ) : (
        draftTreatmentMedicinesTable.map((med, index) => (
          <div key={`treatment-med-edit-${index}`} className="space-y-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <span className="w-6 text-xs font-medium text-gray-500">{index + 1}.</span>
              <Input
                value={med.name ?? ''}
                onChange={(event) =>
                  handleTreatmentMedicineFieldChange(index, 'name', event.target.value)
                }
                placeholder="Medicine name"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove medicine ${index + 1}`}
                onClick={() => handleTreatmentMedicineRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-4 gap-2 pl-8">
              <Input
                value={med.form ?? ''}
                onChange={(event) =>
                  handleTreatmentMedicineFieldChange(index, 'form', event.target.value)
                }
                placeholder="Form (e.g. Churna)"
                className="text-sm"
              />
              <Input
                value={med.dose ?? ''}
                onChange={(event) =>
                  handleTreatmentMedicineFieldChange(index, 'dose', event.target.value)
                }
                placeholder="Dosage"
                className="text-sm"
              />
              <Input
                value={med.frequency ?? ''}
                onChange={(event) =>
                  handleTreatmentMedicineFieldChange(index, 'frequency', event.target.value)
                }
                placeholder="Frequency"
                className="text-sm"
              />
              <Input
                value={med.duration ?? ''}
                onChange={(event) =>
                  handleTreatmentMedicineFieldChange(index, 'duration', event.target.value)
                }
                placeholder="Duration"
                className="text-sm"
              />
              <Input
                value={med.anupana ?? ''}
                onChange={(event) =>
                  handleTreatmentMedicineFieldChange(index, 'anupana', event.target.value)
                }
                placeholder="Anupana (e.g. warm milk)"
                className="text-sm"
              />
              <Input
                value={med.timing ?? ''}
                onChange={(event) =>
                  handleTreatmentMedicineFieldChange(index, 'timing', event.target.value)
                }
                placeholder="Timing (e.g. Morning and night)"
                className="text-sm"
              />
              <Input
                value={med.additional_info ?? ''}
                onChange={(event) =>
                  handleTreatmentMedicineFieldChange(index, 'additional_info', event.target.value)
                }
                placeholder="Additional info"
                className="col-span-2 text-sm"
              />
            </div>
          </div>
        ))
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleTreatmentMedicineAdd}
        leftIcon={<Plus className="h-4 w-4" />}
      >
        Add medicine
      </Button>
    </div>
  );

  // Editor for the structured Personal History form — a fixed set of 8
  // fields (not a row-based table), same 2-column layout as the read-only
  // display grid it replaces while editing.
  const PERSONAL_HISTORY_EDIT_FIELDS: Array<{ key: keyof PersonalHistory; label: string }> = [
    { key: 'diet', label: 'Diet' },
    { key: 'appetite', label: 'Appetite' },
    { key: 'sleep', label: 'Sleep' },
    { key: 'exercise', label: 'Exercise' },
    { key: 'bowel', label: 'Bowel' },
    { key: 'bladder', label: 'Bladder' },
    { key: 'addiction', label: 'Addiction' },
    { key: 'menstrual', label: 'Menstrual' },
  ];

  const handlePersonalHistoryFieldChange = (key: keyof PersonalHistory, value: string) => {
    setDraftPersonalHistory((prev) => ({ ...prev, [key]: value }));
  };

  const renderPersonalHistoryEditor = () => (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      {PERSONAL_HISTORY_EDIT_FIELDS.map(({ key, label }) => (
        <div key={key} className="flex flex-col gap-1">
          <span className="font-hanken text-sm font-semibold text-[#585859]">{label}</span>
          <Input
            value={draftPersonalHistory[key] ?? ''}
            onChange={(event) => handlePersonalHistoryFieldChange(key, event.target.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
            className="text-sm"
          />
        </div>
      ))}
    </div>
  );

  const renderDiagnosisEditor = () => (
    <div className="space-y-4">
      {draftDiagnosisCodes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No diagnoses listed. Add a diagnosis using the button below.
        </p>
      ) : (
        draftDiagnosisCodes.map((item, index) => (
          <div key={`diagnosis-edit-${index}`} className="space-y-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <span className="w-6 text-xs font-medium text-gray-500">{index + 1}.</span>
              <Input
                value={item.symptom}
                onChange={(event) =>
                  handleDiagnosisCodeFieldChange(index, 'symptom', event.target.value)
                }
                placeholder="Diagnosis name"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove diagnosis ${index + 1}`}
                onClick={() => handleDiagnosisCodeRemove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2 pl-8">
              <Input
                value={item.code}
                onChange={(event) =>
                  handleDiagnosisCodeFieldChange(index, 'code', event.target.value)
                }
                placeholder="ICD-11 code (e.g. MD12)"
                className="text-sm"
              />
              <Input
                value={item.description}
                onChange={(event) =>
                  handleDiagnosisCodeFieldChange(index, 'description', event.target.value)
                }
                placeholder="Description"
                className="text-sm"
              />
            </div>
          </div>
        ))
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleDiagnosisCodeAdd}
        leftIcon={<Plus className="h-4 w-4" />}
      >
        Add diagnosis
      </Button>
    </div>
  );

  const summarizerHeadingClassName =
    'font-hanken text-[20px] font-medium leading-[22px] tracking-[-0.16px] text-[#051858]';
  const summarizerBodyClassName =
    'whitespace-pre-wrap pr-20 font-hanken text-base font-normal leading-7 tracking-[-0.16px] text-[#585859]';
  const summarizerFieldContainerClassName =
    'relative mt-2 rounded-[14px] border border-[#e8eaf0] bg-white px-4 py-3';
  const summarizerTriggerClassName = 'w-full !bg-transparent !px-0 !py-0 hover:no-underline';
  const summarizerContentClassName = '!px-0 !pb-0 !pt-0';

  // Reusable read-only accordion section for non-editable content blocks.
  const renderReadOnlySection = (
    title: string,
    value: string,
    content?: React.ReactNode,
    analysisFieldKey?: keyof AIAnalysisResult
  ) => {
    let timestamp: number | null = null;
    if (analysisFieldKey && consultation.aiAnalysis) {
      const analysisField = consultation.aiAnalysis[analysisFieldKey];
      if (analysisField && typeof analysisField === 'object' && 'timestamp' in analysisField) {
        const rawTimestamp = analysisField.timestamp;
        timestamp = parseRawTimestamp(rawTimestamp);
      }
    }

    return (
      <AccordionItem
        value={title.toLowerCase().replace(/\s+/g, '-')}
        className="!border-none !pb-0"
      >
        <AccordionTrigger className={summarizerTriggerClassName}>
          <div className="flex items-center gap-2 text-left">
            <span className={summarizerHeadingClassName}>{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
          </div>
        </AccordionTrigger>
        <AccordionContent className={summarizerContentClassName}>
          <div className={summarizerFieldContainerClassName}>
            {content || (
              <p className={summarizerBodyClassName}>
                {value || `No ${title.toLowerCase()} recorded`}
              </p>
            )}
            {timestamp !== null && timestamp > 0 && (
              <div className="absolute bottom-2 right-2 flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => seekToTimestamp(timestamp!)}
                  className="h-auto gap-1.5 border border-gray-200 bg-white/80 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  title="Click to play from this timestamp"
                >
                  <Clock className="h-3 w-3" />
                  <span>{formatTimestampBadge(timestamp!)}</span>
                </Button>
                <Info className="h-3 w-3 text-gray-400" />
              </div>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2 px-1">
            <button
              type="button"
              className="flex items-center justify-center rounded p-1 text-[#969AAC] hover:bg-gray-100"
            >
              <ThumbUp size={14} fill="#969AAC" />
            </button>
            <button
              type="button"
              className="flex items-center justify-center rounded p-1 text-[#969AAC] hover:bg-gray-100"
            >
              <ThumbDown size={14} fill="#969AAC" />
            </button>
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };
  void renderReadOnlySection;

  // Helper to render General Examination as a bullet list, one <li> per
  // "label: value" entry (flattened from the AI's structured exam entries,
  // e.g. "exam: mild murmur, BP: elevated"), bolding each label.
  const formatExaminationText = (text: string) => {
    const items = splitCommaSeparatedText(text);
    if (items.length === 0) return null;
    return (
      <ul className="list-disc space-y-0.5 pl-5">
        {items.map((item, index) => {
          const { label, value } = splitLabelValueItem(item);
          return (
            <li key={index}>
              {label ? (
                <>
                  <strong>{label}:</strong> {value}
                </>
              ) : (
                value
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  // Helper to render a comma-joined value (non-Ayurveda chief complaint or
  // assessment/diagnosis) as a bullet list — splits e.g. "Fever, chest pain,
  // and cough for 5 days" into one <li> per item, preserving any inline
  // HTML (e.g. <b>) the AI highlighted within an item.
  const formatCommaBullets = (text: string) => {
    const items = splitCommaSeparatedText(text);
    if (items.length === 0) return null;
    return (
      <ul className="list-disc space-y-0.5 pl-5">
        {items.map((item, index) => (
          <li key={index} dangerouslySetInnerHTML={{ __html: sanitizeClinicalHtml(item) }} />
        ))}
      </ul>
    );
  };

  // Family History: one bullet per family member, no leading number — the
  // bullet marker replaces it. getFamilyHistoryItems also handles the
  // already-flattened "1. Relation: X..." text this field is left as once
  // saved (see conclusionParser.ts for why), not just the raw live array.
  const formatFamilyHistoryBullets = (text: string) => {
    const items = getFamilyHistoryItems(text);
    if (items.length === 0) return null;
    return (
      <ul className="list-disc space-y-1.5 pl-5">
        {items.map((item, index) => (
          <li key={index} className="whitespace-pre-line">
            {item}
          </li>
        ))}
      </ul>
    );
  };

  // Medical History is a free-text narrative (AI-extracted sentences
  // concatenated together, sometimes hand-edited), not an itemized list, so
  // it can't be split on commas alone like Chief Complaint — it needs
  // sentence-aware splitting plus per-sentence list-comma splitting, and an
  // embedded "Lab investigations reviewed: ..." clause pulled into its own
  // sub-list instead of staying mixed into the main bullets. See
  // splitMedicalNarrativeToBullets for the full rules.
  const formatMedicalHistoryBullets = (text: string) => {
    const { mainItems, labItems } = splitMedicalNarrativeToBullets(text);
    if (mainItems.length === 0 && labItems.length === 0) return null;
    return (
      <>
        {mainItems.length > 0 && (
          <ul className="list-disc space-y-0.5 pl-5">
            {mainItems.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ __html: sanitizeClinicalHtml(item) }} />
            ))}
          </ul>
        )}
        {labItems.length > 0 && (
          <div className={mainItems.length > 0 ? 'mt-3' : undefined}>
            <p className="font-semibold">Lab Investigations Reviewed:</p>
            <ul className="list-disc space-y-0.5 pl-5">
              {labItems.map((item, index) => (
                <li key={index} dangerouslySetInnerHTML={{ __html: sanitizeClinicalHtml(item) }} />
              ))}
            </ul>
          </div>
        )}
      </>
    );
  };

  // Reusable dropdown component for field options
  const FieldDropdown = ({
    fieldName,
    options,
    label,
  }: {
    fieldName: string;
    options: string[];
    label: string;
  }) => {
    const handleSelect = (value: string) => {
      if (!value) return;

      const formatWithBullet = (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return `• ${value}`;
        // If text already starts with a bullet, keep it as-is
        if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
          return `${trimmed}\n• ${value}`;
        }
        // Otherwise, add bullet to the first line too
        return `• ${trimmed}\n• ${value}`;
      };

      if (editingField === fieldName) {
        // Already editing - just append to current draft
        setDraftText(formatWithBullet(draftText));
      } else {
        // Not editing - enter edit mode first, then append
        const existingContent = (fields[fieldName as keyof typeof fields] as string) || '';
        beginEdit(fieldName as CaseSheetEditableFieldKey);
        setTimeout(() => {
          setDraftText(formatWithBullet(existingContent));
        }, 0);
      }
    };

    return (
      <div className="mt-3">
        <Select onValueChange={handleSelect} value="">
          <SelectTrigger className="w-full">
            <SelectValue placeholder={`+ Add ${label.toLowerCase()} option...`} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option, idx) => (
              <SelectItem key={idx} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  // Ayurveda-only History + Personal History body, matching the live
  // consultation page's Patient Information card. Shared by both render
  // paths for 'medicalHistory' — the standalone (ungrouped) case and the
  // grouped ("Other History" etc.) case — so they never drift out of sync.
  const renderHistoryPersonalHistoryBody = () => {
    const historyFields: Array<{ label: string; value?: string | null }> = [
      { label: 'History of Past Illness', value: consultation.pastIllness },
      { label: 'Treatment History Of', value: consultation.treatmentHistory },
      { label: 'Family History Of', value: consultation.historyFamilyHistory },
      { label: 'Current Medication', value: consultation.currentMedication },
      {
        label: 'Family/ Social/ Occupational',
        value: consultation.familySocialOccupational,
      },
    ].filter(({ value }) => Boolean(value));
    const personalHistory = mergePersonalHistory(
      fields.personalHistory,
      consultation.personalHistory
    );
    const personalHistoryFields: Array<{ label: string; value?: string | null }> = [
      { label: 'Diet', value: personalHistory?.diet },
      { label: 'Appetite', value: personalHistory?.appetite },
      { label: 'Sleep', value: personalHistory?.sleep },
      { label: 'Exercise', value: personalHistory?.exercise },
      { label: 'Bowel', value: personalHistory?.bowel },
      { label: 'Bladder', value: personalHistory?.bladder },
      { label: 'Addiction', value: personalHistory?.addiction },
      { label: 'Menstrual', value: personalHistory?.menstrual },
    ];
    const hasPersonalHistory = personalHistoryFields.some(({ value }) => Boolean(value));

    return (
      <div className="space-y-4">
        <div>
          <div className="mb-2 flex min-h-[28px] items-center justify-between gap-2">
            <span className="font-hanken text-[16px] font-medium leading-[20px] tracking-[-0.16px] text-[#051858]">
              Medical & Family History
            </span>
            {canEdit && (
              <div className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
                {renderHeaderActions('medicalHistory')}
              </div>
            )}
          </div>
          {editingField === 'medicalHistory' ? (
            <div className={summarizerFieldContainerClassName}>
              <Textarea
                value={draftText}
                onChange={(_, value) => setDraftText(value)}
                autoResize
                minRows={4}
                placeholder="Enter medical history"
              />
            </div>
          ) : (
            <>
              <div className={summarizerFieldContainerClassName}>
                {historyFields.length > 0 ? (
                  <ul className="list-disc space-y-0.5 pl-5 font-hanken text-[#585859]">
                    {historyFields.map(({ label, value }) => (
                      <li key={label}>
                        <span className="font-semibold">{label}:</span> {value}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={summarizerBodyClassName}>No history recorded</p>
                )}
              </div>
              {renderFeedbackButtons()}
            </>
          )}
        </div>
        <div>
          <div className="mb-2 flex min-h-[28px] items-center justify-between gap-2">
            <span className="font-hanken text-[16px] font-medium leading-[20px] tracking-[-0.16px] text-[#051858]">
              Personal History
            </span>
            {canEdit && (
              <div className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
                {renderHeaderActions('personalHistory')}
              </div>
            )}
          </div>
          {editingField === 'personalHistory' ? (
            renderPersonalHistoryEditor()
          ) : (
            <>
              <div className={summarizerFieldContainerClassName}>
                {hasPersonalHistory ? (
                  <div className="grid grid-cols-2 gap-x-10 gap-y-2">
                    {personalHistoryFields.map(({ label, value }) => (
                      <div key={label} className="flex flex-col text-[#585859]">
                        <span className="font-hanken text-sm font-semibold">{label}:</span>
                        <span className="font-hanken text-sm">{value || '—'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={summarizerBodyClassName}>No personal history recorded</p>
                )}
              </div>
              {renderFeedbackButtons()}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTextSection = (field: TextFieldKey, title: string) => {
    const timestamp = getFieldTimestamp(field);
    const dropdownOptions = getDropdownOptions(field);

    return (
      <AccordionItem value={field} className="!border-none !pb-0">
        <AccordionTrigger className={summarizerTriggerClassName}>
          <div className="flex items-center gap-2 text-left">
            <span className={summarizerHeadingClassName}>{title}</span>
          </div>
          {/* Edit action positioned before the chevron */}
          <div className="flex items-center gap-2">
            {canEdit && (
              <div onClick={(e) => e.stopPropagation()}>{renderHeaderActions(field)}</div>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
          </div>
        </AccordionTrigger>
        <AccordionContent className={summarizerContentClassName}>
          {editingField === field ? (
            <div className="space-y-3">
              {/* Dropdown for predefined options */}
              {dropdownOptions && dropdownOptions.length > 0 && (
                <FieldDropdown fieldName={field} options={dropdownOptions} label={title} />
              )}
              {/* Textarea for manual editing */}
              <Textarea
                value={draftText}
                onChange={(_, value) => setDraftText(value)}
                autoResize
                minRows={4}
                placeholder={`Enter ${title.toLowerCase()}`}
              />
            </div>
          ) : (
            <>
              <div className={summarizerFieldContainerClassName}>
                <div className={summarizerBodyClassName}>
                  {field === 'generalExamination' &&
                  (fields.generalExamination || fallbackTextValues.generalExamination)?.trim() ? (
                    formatExaminationText(getDisplayText(field))
                  ) : field === 'adtOrder' && (fields.adtOrder || fallbackTextValues.adtOrder) ? (
                    formatAdtOrderText(getDisplayText(field))
                  ) : field === 'familyHistory' &&
                    (fields.familyHistory || fallbackTextValues.familyHistory)?.trim() ? (
                    formatFamilyHistoryBullets(getDisplayText(field))
                  ) : field === 'medicalHistory' &&
                    (fields.medicalHistory || fallbackTextValues.medicalHistory)?.trim() ? (
                    formatMedicalHistoryBullets(getDisplayText(field))
                  ) : (field === 'chiefComplaint' ||
                      field === 'assessmentDiagnosis' ||
                      field === 'allergies') &&
                    (field === 'chiefComplaint'
                      ? fields.chiefComplaint || fallbackTextValues.chiefComplaint
                      : field === 'assessmentDiagnosis'
                        ? fields.assessmentDiagnosis || fallbackTextValues.assessmentDiagnosis
                        : fields.allergies || fallbackTextValues.allergies
                    )?.trim() ? (
                    formatCommaBullets(getDisplayText(field))
                  ) : (
                    // Every other free-text field (Social History, Review of Systems,
                    // Treatment Plan, Doctor's Notes, Diet Advice, Follow Up Plan,
                    // Pediatric Milestone, Clinical Symptoms, etc.) renders as bullets
                    // too — one per sentence/list-comma, same splitting rules as
                    // Medical History minus its "Lab investigations reviewed:"
                    // sub-list detection. Only applied when there's real content —
                    // an empty-message placeholder (e.g. "No social history
                    // recorded") still has non-comma, non-newline text, so
                    // splitProseToBullets would wrap it in its own single-item
                    // bullet otherwise; render that case as plain text instead.
                    <span
                      className="[&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-4 [&_p]:my-0.5 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeClinicalHtml(
                          (fields[field] || fallbackTextValues[field])?.trim()
                            ? proseTextToHtmlList(getDisplayText(field)) || getDisplayText(field)
                            : getDisplayText(field)
                        ),
                      }}
                    />
                  )}
                </div>
                {/* Timestamp Badge — only shown when a real timestamp exists (> 0) */}
                {timestamp !== null && timestamp > 0 && (
                  <div className="absolute bottom-2 right-2 flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => seekToTimestamp(timestamp!)}
                      className="h-auto gap-1.5 border border-gray-200 bg-white/80 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      title="Click to play from this timestamp"
                    >
                      <Clock className="h-3 w-3" />
                      <span>{formatTimestampBadge(timestamp!)}</span>
                    </Button>
                    <Info className="h-3 w-3 text-gray-400" />
                  </div>
                )}
              </div>
              {/* Dropdown for predefined options - below the text */}
              {dropdownOptions && dropdownOptions.length > 0 && (
                <FieldDropdown fieldName={field} options={dropdownOptions} label={title} />
              )}
              {renderFeedbackButtons()}
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    );
  };

  // Extract investigations data (lab tests + radiology)
  const labTestsText = consultation.aiAnalysis
    ? getAnalysisFieldValue(consultation.aiAnalysis.lab_tests) || ''
    : '';
  const investigationOrdersText = consultation.aiAnalysis
    ? extractInvestigationsOrders(consultation.aiAnalysis.investigations_orders)
    : '';
  const radiologyModality = consultation.aiAnalysis?.radiology
    ? getAnalysisFieldValue(consultation.aiAnalysis.radiology.modality) || ''
    : '';
  const radiologyProcedure = consultation.aiAnalysis?.radiology
    ? getAnalysisFieldValue(consultation.aiAnalysis.radiology.service_procedure) || ''
    : '';
  const radiologyText = [radiologyModality, radiologyProcedure]
    .filter((item) => typeof item === 'string' && item.trim().length > 0)
    .join(': ');

  // Combine all investigation parts, ensuring all are strings and filtering out empty values
  const investigationsText =
    fields.investigations ||
    [investigationOrdersText, labTestsText, radiologyText]
      .filter((segment) => typeof segment === 'string' && segment.trim().length > 0)
      .map((segment) => String(segment).trim())
      .join('. ');

  // Split into individual bullet entries for display — instead of rendering the
  // whole block (which may already be one comma-joined string, see CaseSheetPage's
  // getSessionFieldValue) as a single flowing paragraph.
  const investigationsItems = splitInvestigationsText(investigationsText);

  const handleSendWhatsApp = useCallback(async () => {
    if (!patient.phoneNumber) {
      toast.error('Patient phone number is not available');
      return;
    }
    if (!consultationId || !user?.id) {
      toast.error('Missing encounter or practitioner information');
      return;
    }

    setIsSendingWhatsApp(true);
    try {
      const addressParts = [
        patient.address,
        patient.city,
        patient.state,
        patient.postalCode,
        patient.country,
      ].filter(Boolean);
      const fullAddress = addressParts.join(', ');
      const userName =
        user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.username || '';

      toast.info('Generating prescription...');
      const generateResult = await generatePrescription({
        patientName: patient.name,
        patientPhone: patient.phoneNumber,
        patientAge: patient.age,
        patientGender: formatGenderDisplay(patient.gender),
        patientDateOfBirth: patient.dateOfBirth || undefined,
        patientAddress: fullAddress || undefined,
        patientIdentifier: patient.identifier,
        practitionerId: user.id,
        doctorName: userName,
        encounterId: consultationId,
        consultationDate: new Date(consultation.createdAt).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
        vitals: consultation.vitals
          ? {
              height: consultation.vitals.height,
              weight: consultation.vitals.weight,
              temperature: consultation.vitals.temperature,
              bloodPressure: consultation.vitals.bloodPressure,
              heartRate: consultation.vitals.heartRate,
              respiratoryRate: consultation.vitals.respiratoryRate,
              oxygenSaturation: consultation.vitals.oxygenSaturation,
            }
          : undefined,
        chiefComplaint: fields.chiefComplaint || '',
        diagnosis: diagnosisText || '',
        medications: displayMedicationsForPrescription,
        investigations: investigationsText || undefined,
        doctorNotes: fields.doctorNotes || undefined,
      });

      toast.info('Sending to WhatsApp...');
      await sendPrescription({
        filePath: generateResult.data.filePath,
        patientPhone: patient.phoneNumber,
      });

      toast.success('Prescription sent to WhatsApp successfully!');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send prescription';
      logger.error('WhatsApp prescription send failed:', err);
      toast.error(message);
    } finally {
      setIsSendingWhatsApp(false);
    }
  }, [
    patient,
    user,
    consultationId,
    consultation,
    fields,
    diagnosisText,
    displayMedications,
    investigationsText,
  ]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="p-6">
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
              <p className="text-muted-foreground">Loading case sheet...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="p-6">
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <p className="mb-4 text-destructive">{error}</p>
              {actions.onBack && (
                <Button onClick={actions.onBack} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Go Back
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#E6E8EF] print:h-auto print:overflow-visible print:bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 print:hidden">
        <div className="flex items-center gap-4">
          <img
            src={PranikLogo}
            alt="Pranik.AI Logo"
            style={{
              height: '32px',
              width: 'auto',
            }}
            className="object-contain"
          />
        </div>

        <div className="flex items-center gap-4">
          {/* Lab Reports button — beside Patient Details */}
          {patientId && <LabReportsModal patientId={patientId} />}

          {actions.onCopy && (
            <button
              onClick={actions.onCopy}
              className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-[calc(12px*var(--app-scale))] transition-colors hover:bg-[#D1DBFD]"
              aria-label="Copy case sheet summary"
            >
              <Copy className="h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] text-[#6B8AF9]" />
            </button>
          )}
          <button
            onClick={actions.onExport}
            className="flex items-center justify-center rounded-lg border border-[#EAEBF3] bg-[#EEF6FF] p-[calc(12px*var(--app-scale))] transition-colors hover:bg-[#D1DBFD]"
            aria-label="Download case sheet as PDF"
          >
            <Download className="h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] text-[#6B8AF9]" />
          </button>
          <div className="relative">
            <button
              className="flex items-center gap-3 rounded-2xl bg-[#f8f9fb] px-4 py-2 transition-colors hover:bg-[#e8e9eb]"
              onMouseEnter={() => setShowPatientDetails(true)}
              onMouseLeave={() => setShowPatientDetails(false)}
            >
              <span className="text-base font-medium text-[#586695]">Patient Details</span>
              <div className="flex items-center justify-center rounded-full">
                <AccountCircle size={24} fill="#2E3E6F" />
              </div>
            </button>

            {showPatientDetails && patient && (
              <div className="absolute right-0 top-full z-50 mt-2 min-w-[280px] rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
                <div className="space-y-2">
                  <div className="border-b border-gray-200 pb-2">
                    <p className="text-sm font-semibold text-gray-700">Patient Information</p>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Name:</span>
                      <span className="text-sm font-medium text-gray-900">{patient.name}</span>
                    </div>
                    {patient.age !== undefined && patient.age > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Age:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {patient.age} years
                        </span>
                      </div>
                    )}
                    {patient.gender && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Gender:</span>
                        <span className="text-sm font-medium capitalize text-gray-900">
                          {formatGenderDisplay(patient.gender)}
                        </span>
                      </div>
                    )}
                    {patient.phoneNumber && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Phone:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {patient.phoneNumber}
                        </span>
                      </div>
                    )}
                    {patient.email && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Email:</span>
                        <span className="text-sm font-medium text-gray-900">{patient.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="flex h-0 flex-1 flex-col overflow-hidden px-2 py-1">
        <ResizablePanelGroup direction="horizontal" className="gap-2">
          {/* LEFT PANEL - Main Content with Accordions */}
          <ResizablePanel
            defaultSize={68}
            minSize={40}
            className="relative rounded-3xl border border-white bg-[#E6E8EF]"
          >
            <div className="scrollbar-thin pointer-events-auto flex h-full w-full flex-col overflow-y-auto pb-32">
              {/* White content card wrapping header + accordion to match Figma */}
              <div className="rounded-2xl bg-[#E6E8EF] px-5 pb-3 pt-5">
                {/* Header */}
                <div className="mb-5">
                  <h1 className="font-serif text-[24px] font-medium leading-[30px] tracking-[-0.15px] text-[#1F3959]">
                    Summarizer
                  </h1>
                  <p className="mt-1 font-hanken text-[14px] font-medium leading-5 tracking-[-0.16px] text-[#585859]">
                    This information is summarized from the conversation, by AI. Verify before you
                    save the details
                  </p>
                </div>

                {/* Accordion Sections - Dynamically rendered based on department config */}
                <Accordion
                  type="multiple"
                  className="flex-1 space-y-6"
                  defaultValue={[
                    ...effectiveFieldOrder,
                    'icd11-diagnosis',
                    ...(isAyurveda ? ['medicalHistory'] : []),
                  ]}
                >
                  {(() => {
                    // Collect all fields already covered by groups so standalone duplicates are skipped.
                    const fieldsInGroups = new Set<string>(
                      fieldGroups ? Object.values(fieldGroups).flat() : []
                    );
                    // Track fields already rendered inside any group to prevent cross-group duplication.
                    const renderedGroupFields = new Set<string>();

                    return effectiveFieldOrder
                      .filter((section) => !(isAyurveda && AYURVEDA_HIDDEN_SECTIONS.has(section)))
                      .map((section) => {
                        if (isAyurveda && section === 'viruddha-ahaara') {
                          return renderAyurvedaRecommendationBlock('viruddha-ahaara');
                        }

                        // Skip standalone entry if it is already rendered inside a group
                        if (
                          !fieldGroups?.[section] && // not itself a group key
                          fieldsInGroups.has(section) // but covered by a group
                        ) {
                          return null;
                        }

                        // Check if this is a grouped section
                        const isGrouped = fieldGroups && section in fieldGroups;

                        // Handle grouped sections - render all fields flat inside one accordion
                        if (isGrouped) {
                          // The Treatment group parent heading is always "Treatment".
                          // Children render their own labels ("Medication", "Treatment Plan").
                          // Previously this showed "Treatment Plan" for most departments, which
                          // duplicated the child label; the parent is now consistently "Treatment".
                          const groupLabel =
                            section === 'treatmentPlan' ? 'Treatment' : getFieldLabel(section);
                          const groupFields = fieldGroups[section];
                          return (
                            <AccordionItem
                              key={section}
                              value={section}
                              className="!border-none !pb-0"
                            >
                              <AccordionTrigger className={summarizerTriggerClassName}>
                                <div className="flex items-center gap-2 text-left">
                                  <span className={summarizerHeadingClassName}>{groupLabel}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className={`${summarizerContentClassName} !pt-4`}>
                                <div className="space-y-4">
                                  {groupFields.map((field, idx) => {
                                    // Cross-group deduplication: skip if this field was already rendered
                                    // in a previous group (prevents treatmentPlan appearing twice).
                                    if (renderedGroupFields.has(field)) return null;
                                    renderedGroupFields.add(field);

                                    // Same hidden-for-Ayurveda sections as the
                                    // top-level filter above — a field can ALSO
                                    // reach this point via a group's groupFields
                                    // array (e.g. nested inside "Other
                                    // History"), which bypasses that top-level
                                    // filter, so it needs to be checked here too.
                                    if (isAyurveda && AYURVEDA_HIDDEN_SECTIONS.has(field)) {
                                      return null;
                                    }

                                    // Ayurveda-only: History is rendered as its own
                                    // top-level "History" section (see after this
                                    // loop), not nested inside this group — skip it
                                    // here so it doesn't also render/duplicate here.
                                    if (field === 'medicalHistory' && isAyurveda) {
                                      return null;
                                    }

                                    // Treat 'medications' as an alias for 'prescribedMedications' so the
                                    // Ayurveda department config key renders the medication table correctly.
                                    const effectiveField =
                                      field === 'medications' ? 'prescribedMedications' : field;

                                    const fieldLabel = getFieldLabel(field, section);
                                    const fieldEmptyMessage = getFieldEmptyMessage(
                                      effectiveField as TextFieldKey,
                                      section
                                    );
                                    // Handle prescribedMedications (medication table) specially;
                                    // also catches 'medications' alias from Ayurveda department config.
                                    if (
                                      field === 'prescribedMedications' ||
                                      field === 'medications'
                                    ) {
                                      return (
                                        <div key={field}>
                                          {idx > 0 && (
                                            <div className="my-4 border-t border-gray-200" />
                                          )}
                                          <div className={summarizerFieldContainerClassName}>
                                            <div className="mb-1 flex items-center justify-between gap-2">
                                              <p className="font-hanken text-sm font-semibold leading-7 tracking-[-0.16px] text-[#051858]">
                                                {fieldLabel}
                                              </p>
                                              {canEdit &&
                                                renderHeaderActions('prescribedMedications')}
                                            </div>
                                            {editingField === 'prescribedMedications' ? (
                                              renderMedicationEditor()
                                            ) : displayMedications &&
                                              displayMedications.length > 0 ? (
                                              <div className="w-full overflow-x-auto">
                                                <table className="w-full min-w-[960px] border-collapse text-sm">
                                                  <thead>
                                                    <tr className="border-b border-gray-200 text-left">
                                                      <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                        Generic name/Strength/Form
                                                      </th>
                                                      <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                        Type
                                                      </th>
                                                      <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                        Dosage
                                                      </th>
                                                      <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                        Schedule
                                                      </th>
                                                      <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                        Route
                                                      </th>
                                                      <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                        Days
                                                      </th>
                                                      <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                        Relation with meal
                                                      </th>
                                                      <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                        Comments
                                                      </th>
                                                      <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                        Item Code
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {displayMedications.map(
                                                      (
                                                        med: SimplifiedMedication,
                                                        medIdx: number
                                                      ) => (
                                                        <tr
                                                          key={`med-${medIdx}`}
                                                          className="border-b border-gray-100 hover:bg-gray-50/60"
                                                        >
                                                          <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                            {med.medication}
                                                          </td>
                                                          <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                            {med.medicineType || '—'}
                                                          </td>
                                                          <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                            {med.dosage || '—'}
                                                          </td>
                                                          <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                            {med.frequency || '—'}
                                                          </td>
                                                          <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                            {med.route || '—'}
                                                          </td>
                                                          <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                            {med.duration || '—'}
                                                          </td>
                                                          <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                            {med.relationWithMeal || '—'}
                                                          </td>
                                                          <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                            {med.additionalInfo || '—'}
                                                          </td>
                                                          <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                            {med.itemCode || '—'}
                                                          </td>
                                                        </tr>
                                                      )
                                                    )}
                                                  </tbody>
                                                </table>
                                              </div>
                                            ) : (
                                              <p className="text-sm text-muted-foreground">
                                                No prescribed medications recorded
                                              </p>
                                            )}
                                          </div>
                                          {renderFeedbackButtons()}
                                        </div>
                                      );
                                    }
                                    // Handle regular text fields
                                    const displayValue = getDisplayText(
                                      effectiveField as TextFieldKey,
                                      section
                                    );
                                    const isEmpty =
                                      !displayValue || displayValue === fieldEmptyMessage;
                                    return (
                                      <div key={field}>
                                        {idx > 0 && (
                                          <div className="my-4 border-t border-gray-200" />
                                        )}
                                        {/* Field label + edit icon above the content box — consistent with standalone sections */}
                                        {/* When sub-field name equals the group key the label would duplicate the group header —
                                          use a disambiguating label instead of hiding it entirely. */}
                                        <div className="mb-2 flex min-h-[28px] items-center justify-between gap-2">
                                          <span className="font-hanken text-[16px] font-medium leading-[20px] tracking-[-0.16px] text-[#051858]">
                                            {/* Use the department-aware fieldLabel directly. The parent
                                              group is now consistently "Treatment" so there is no
                                              duplicate-label risk; hardcoding "Treatment Plan"
                                              here would override department customizations (e.g.
                                              "Patient Education" for Pediatrics). */}
                                            {fieldLabel}
                                          </span>
                                          {canEdit && (
                                            <div
                                              className="flex shrink-0 items-center"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {renderHeaderActions(
                                                effectiveField as CaseSheetEditableFieldKey
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        <div className={summarizerFieldContainerClassName}>
                                          {editingField === effectiveField ? (
                                            <>
                                              {/* Dropdown for predefined options - above textarea in edit mode */}
                                              {getDropdownOptions(effectiveField) &&
                                                getDropdownOptions(effectiveField)!.length > 0 && (
                                                  <FieldDropdown
                                                    fieldName={effectiveField}
                                                    options={getDropdownOptions(effectiveField)!}
                                                    label={fieldLabel}
                                                  />
                                                )}
                                              <Textarea
                                                value={draftText}
                                                onChange={(_, value) => setDraftText(value)}
                                                autoResize
                                                minRows={4}
                                                placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                                              />
                                            </>
                                          ) : (
                                            <div className={summarizerBodyClassName}>
                                              {isEmpty ? (
                                                fieldEmptyMessage
                                              ) : effectiveField === 'familyHistory' ? (
                                                formatFamilyHistoryBullets(displayValue)
                                              ) : effectiveField === 'medicalHistory' ? (
                                                formatMedicalHistoryBullets(displayValue)
                                              ) : effectiveField === 'chiefComplaint' ||
                                                effectiveField === 'assessmentDiagnosis' ||
                                                effectiveField === 'allergies' ? (
                                                formatCommaBullets(displayValue)
                                              ) : effectiveField === 'generalExamination' ? (
                                                formatExaminationText(displayValue)
                                              ) : (
                                                // Every other grouped text field (Family-Social-Occupational,
                                                // Diet Advice, Follow Up Plan, etc.) renders as bullets too —
                                                // same sentence/list-comma splitting as the standalone fields.
                                                <span
                                                  className="[&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-4 [&_p]:my-0.5 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6"
                                                  dangerouslySetInnerHTML={{
                                                    __html: sanitizeClinicalHtml(
                                                      proseTextToHtmlList(displayValue) ||
                                                        displayValue
                                                    ),
                                                  }}
                                                />
                                              )}
                                            </div>
                                          )}
                                          {/* Dropdown for predefined options - below the text in view mode */}
                                          {editingField !== effectiveField &&
                                            getDropdownOptions(effectiveField) &&
                                            getDropdownOptions(effectiveField)!.length > 0 && (
                                              <FieldDropdown
                                                fieldName={effectiveField}
                                                options={getDropdownOptions(effectiveField)!}
                                                label={fieldLabel}
                                              />
                                            )}
                                        </div>
                                        {renderFeedbackButtons()}
                                      </div>
                                    );
                                  })}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        }

                        // Handle special cases for non-grouped sections

                        // Ayurveda-only: Chief Complaint as bullet points, with
                        // Duration/Mode of Onset alongside — matches the live
                        // consultation page's Patient Information card exactly.
                        if (section === 'chiefComplaint' && isAyurveda) {
                          const complaints = summarizeChiefComplaint(
                            getDisplayText('chiefComplaint')
                          );
                          return (
                            <AccordionItem
                              key={section}
                              value={section}
                              className="!border-none !pb-0"
                            >
                              <AccordionTrigger className={summarizerTriggerClassName}>
                                <div className="flex items-center gap-2 text-left">
                                  <span className={summarizerHeadingClassName}>
                                    Chief Complaint
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {canEdit && (
                                    <div onClick={(e) => e.stopPropagation()}>
                                      {renderHeaderActions('chiefComplaint')}
                                    </div>
                                  )}
                                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className={`${summarizerContentClassName} !pt-4`}>
                                {editingField === 'chiefComplaint' ? (
                                  <Textarea
                                    value={draftText}
                                    onChange={(_, value) => setDraftText(value)}
                                    autoResize
                                    minRows={4}
                                    placeholder="Enter chief complaint"
                                  />
                                ) : (
                                  <>
                                    <div className={summarizerFieldContainerClassName}>
                                      {complaints.length > 0 ? (
                                        <ul className="list-disc space-y-0.5 pl-5 font-hanken text-[#585859]">
                                          {complaints.map((complaint) => (
                                            <li key={complaint}>{complaint}</li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <p className="text-sm italic text-muted-foreground">
                                          No chief complaint recorded
                                        </p>
                                      )}
                                    </div>
                                    {renderFeedbackButtons()}
                                  </>
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        }

                        // Ayurveda-only: History + Personal History, matching
                        // the live consultation page's Patient Information card.
                        if (section === 'prescribedMedications') {
                          return (
                            <AccordionItem
                              key={section}
                              value={section}
                              className="!border-none !pb-0"
                            >
                              <AccordionTrigger className={summarizerTriggerClassName}>
                                <div className="flex items-center gap-2 text-left">
                                  <span className={summarizerHeadingClassName}>Medication</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {canEdit && (
                                    <div onClick={(e) => e.stopPropagation()}>
                                      {renderHeaderActions('prescribedMedications')}
                                    </div>
                                  )}
                                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className={summarizerContentClassName}>
                                {editingField === 'prescribedMedications' ? (
                                  renderMedicationEditor()
                                ) : (
                                  <>
                                    <div className={summarizerFieldContainerClassName}>
                                      {displayMedications && displayMedications.length > 0 ? (
                                        <div className="w-full overflow-x-auto">
                                          <table className="w-full min-w-[960px] border-collapse text-sm">
                                            <thead>
                                              <tr className="border-b border-gray-200 text-left">
                                                <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                  Generic name/Strength/Form
                                                </th>
                                                <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                  Type
                                                </th>
                                                <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                  Dosage
                                                </th>
                                                <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                  Schedule
                                                </th>
                                                <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                  Route
                                                </th>
                                                <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                  Days
                                                </th>
                                                <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                  Relation with meal
                                                </th>
                                                <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                  Comments
                                                </th>
                                                <th className="whitespace-nowrap px-3 py-2.5 align-middle font-hanken font-medium text-[#051858]">
                                                  Item Code
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {displayMedications.map(
                                                (med: SimplifiedMedication, idx: number) => (
                                                  <tr
                                                    key={`med-${idx}`}
                                                    className="border-b border-gray-100 hover:bg-gray-50/60"
                                                  >
                                                    <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                      {med.medication}
                                                    </td>
                                                    <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                      {med.medicineType || '—'}
                                                    </td>
                                                    <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                      {med.dosage || '—'}
                                                    </td>
                                                    <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                      {med.frequency || '—'}
                                                    </td>
                                                    <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                      {med.route || '—'}
                                                    </td>
                                                    <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                      {med.duration || '—'}
                                                    </td>
                                                    <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                      {med.relationWithMeal || '—'}
                                                    </td>
                                                    <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                      {med.additionalInfo || '—'}
                                                    </td>
                                                    <td className="break-words px-3 py-2.5 align-top font-hanken text-[#585859]">
                                                      {med.itemCode || '—'}
                                                    </td>
                                                  </tr>
                                                )
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <p className="text-sm text-muted-foreground">
                                          No prescribed medications recorded
                                        </p>
                                      )}
                                      {/* Medication Timestamp Badge */}
                                      {(() => {
                                        let ts: number | null = null;
                                        const medicationsField =
                                          consultation.aiAnalysis?.medications;
                                        if (medicationsField) {
                                          if (
                                            typeof medicationsField === 'object' &&
                                            'timestamp' in medicationsField
                                          ) {
                                            const rawTs = (
                                              medicationsField as { timestamp?: unknown }
                                            ).timestamp;
                                            ts = parseRawTimestamp(rawTs);
                                          } else if (
                                            Array.isArray(medicationsField) &&
                                            medicationsField.length > 0 &&
                                            medicationsField[0].name?.timestamp
                                          ) {
                                            const rawTs = medicationsField[0].name.timestamp;
                                            ts = parseRawTimestamp(rawTs);
                                          }
                                        }
                                        // Only render badge when a real timestamp exists
                                        if (ts === null || ts <= 0) return null;
                                        return (
                                          <div className="absolute bottom-2 right-2 flex items-center gap-1">
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => seekToTimestamp(ts!)}
                                              className="h-auto gap-1.5 border border-gray-200 bg-white/80 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                              title="Click to play from this timestamp"
                                            >
                                              <Clock className="h-3 w-3" />
                                              <span>{formatTimestampBadge(ts!)}</span>
                                            </Button>
                                            <Info className="h-3 w-3 text-gray-400" />
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    {renderFeedbackButtons()}
                                  </>
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        }

                        // Handle vitals
                        if (section === 'vitals') {
                          const vitals = consultation.vitals;
                          const formattedVitals = vitals ? formatVitals(vitals) : [];

                          return (
                            <AccordionItem
                              key="vitals"
                              value="vitals"
                              className="!border-none !pb-0"
                            >
                              <AccordionTrigger className={summarizerTriggerClassName}>
                                <div className="flex items-center gap-2 text-left">
                                  <span className={summarizerHeadingClassName}>
                                    {isAyurveda ? 'General Examination' : 'Vitals'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div onClick={(e) => e.stopPropagation()}>
                                    {renderVitalsHeaderActions()}
                                  </div>
                                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className={summarizerContentClassName}>
                                <div className={summarizerFieldContainerClassName}>
                                  {isEditingVitals ? (
                                    <VitalsForm
                                      initialVitals={consultation.vitals}
                                      onSubmit={(nextVitals) => {
                                        onVitalsChange?.(nextVitals);
                                        setIsEditingVitals(false);
                                      }}
                                      onClear={() => {
                                        onVitalsChange?.({});
                                        setIsEditingVitals(false);
                                      }}
                                      isLoading={isSavingVitals}
                                    />
                                  ) : (
                                    <>
                                      {formattedVitals.length > 0 ? (
                                        <ul className="list-disc space-y-2 pl-5">
                                          {formattedVitals.map((vital, idx) => (
                                            <li key={idx} className={summarizerBodyClassName}>
                                              {vital.replace(/^-\s*/, '')}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <p className={summarizerBodyClassName}>
                                          No vitals recorded
                                        </p>
                                      )}
                                    </>
                                  )}
                                </div>
                                {!isEditingVitals && renderFeedbackButtons()}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        }

                        // Handle ADT Orders (only show if showadtOrder is true)
                        if (section === 'adtOrder') {
                          if (!shouldShowadtOrder) {
                            return null;
                          }
                          return (
                            <div key={section}>
                              {renderTextSection('adtOrder' as TextFieldKey, 'ADT Orders')}
                            </div>
                          );
                        }

                        // Handle all other text fields
                        if (section in fieldLabels || section in departmentFieldLabels) {
                          const label = getFieldLabel(section);
                          // assessmentDiagnosis renders both the free-text "Assessment /
                          // Diagnosis" bullets and the ICD-11 Diagnosis table below it.
                          if (section === 'assessmentDiagnosis') {
                            return (
                              <Fragment key={section}>
                                {renderTextSection(
                                  'assessmentDiagnosis' as TextFieldKey,
                                  'Assessment / Diagnosis'
                                )}
                                <AccordionItem
                                  value="icd11-diagnosis"
                                  className="!border-none !pb-0"
                                >
                                  <AccordionTrigger className={summarizerTriggerClassName}>
                                    <div className="flex items-center gap-2 text-left">
                                      <span className={summarizerHeadingClassName}>Diagnosis</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {canEdit && (
                                        <div onClick={(e) => e.stopPropagation()}>
                                          {renderHeaderActions('diagnosisCodes')}
                                        </div>
                                      )}
                                      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent className={summarizerContentClassName}>
                                    {editingField === 'diagnosisCodes' ? (
                                      <div className="mt-2">{renderDiagnosisEditor()}</div>
                                    ) : (
                                      <div className="mt-2 overflow-hidden rounded-xl border border-[#EAEBF3] bg-white">
                                        <table className="w-full border-collapse text-sm">
                                          <thead>
                                            <tr className="border-b border-[#EAEBF3]">
                                              <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken font-semibold text-[#051858]">
                                                Name
                                              </th>
                                              <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken font-semibold text-[#051858]">
                                                Type
                                              </th>
                                              <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken font-semibold text-[#051858]">
                                                Code
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {displayDiagnosisCodes.length > 0 ? (
                                              displayDiagnosisCodes.map((item, idx) => (
                                                <tr
                                                  key={idx}
                                                  className="border-b border-[#EAEBF3] last:border-b-0"
                                                >
                                                  <td className="break-words px-4 py-2.5 align-top font-hanken text-[#051858]">
                                                    {item.symptom}
                                                  </td>
                                                  <td className="break-words px-4 py-2.5 align-top font-hanken text-[#717072]">
                                                    {item.type || 'ICD-11'}
                                                  </td>
                                                  <td className="break-words px-4 py-2.5 align-top font-hanken text-[#717072]">
                                                    {item.description
                                                      ? `${item.code} — ${item.description}`
                                                      : item.code}
                                                  </td>
                                                </tr>
                                              ))
                                            ) : (
                                              <tr>
                                                <td
                                                  colSpan={3}
                                                  className="px-4 py-3 text-center font-hanken text-[#B2B8CE]"
                                                >
                                                  No diagnosis recorded
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                    {renderFeedbackButtons()}
                                  </AccordionContent>
                                </AccordionItem>
                              </Fragment>
                            );
                          }
                          return (
                            <div key={section}>
                              {renderTextSection(section as TextFieldKey, label)}
                            </div>
                          );
                        }

                        // Unknown field - skip silently
                        return null;
                      });
                  })()}
                  {/* History — Ayurveda-only, always its own top-level section
                      (never nested inside a group), matching the live
                      consultation page's Patient Information card. History
                      and Personal History are two separate white boxes
                      inside this one section, not their own top-level
                      accordion items. */}
                  {isAyurveda && (
                    <AccordionItem value="medicalHistory" className="!border-none !pb-0">
                      <AccordionTrigger className={summarizerTriggerClassName}>
                        <div className="flex items-center gap-2 text-left">
                          <span className={summarizerHeadingClassName}>History</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className={`${summarizerContentClassName} !pt-4`}>
                        {renderHistoryPersonalHistoryBody()}
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  {/* Diagnosis — Ayurveda-only, Ayurvedic/Allopathic split
                      matching the live consultation page's Diagnosis tiles.
                      Always shown for Ayurveda (with "No data available"
                      placeholders), regardless of whether a diagnosis was
                      captured — same convention as History. */}
                  {isAyurveda && (
                    <AccordionItem value="diagnosis" className="!border-none !pb-0">
                      <AccordionTrigger className={summarizerTriggerClassName}>
                        <div className="flex items-center gap-2 text-left">
                          <span className={summarizerHeadingClassName}>Diagnosis</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className={`${summarizerContentClassName} !pt-4`}>
                        <div className="space-y-4">
                          {(
                            [
                              ['diagnosisAyurvedic', 'Ayurvedic'],
                              ['diagnosisAllopathic', 'Allopathic'],
                            ] as const
                          ).map(([field, label]) => (
                            <div key={field}>
                              <div className="mb-2 flex min-h-[28px] items-center justify-between gap-2">
                                <span className="font-hanken text-[16px] font-medium leading-[20px] tracking-[-0.16px] text-[#051858]">
                                  {label}
                                </span>
                                {canEdit && (
                                  <div
                                    className="flex shrink-0 items-center"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {renderHeaderActions(field)}
                                  </div>
                                )}
                              </div>
                              {editingField === field ? (
                                <div className={summarizerFieldContainerClassName}>
                                  <Textarea
                                    value={draftText}
                                    onChange={(_, value) => setDraftText(value)}
                                    autoResize
                                    minRows={2}
                                    placeholder={`Enter ${label.toLowerCase()} diagnosis`}
                                  />
                                </div>
                              ) : (
                                <>
                                  <div className={summarizerFieldContainerClassName}>
                                    <p className={summarizerBodyClassName}>
                                      {getDisplayText(field)}
                                    </p>
                                  </div>
                                  {renderFeedbackButtons()}
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  {/* Treatment — Ayurveda-only, Medicines/Treatment Principle/
                      Treatment Given nested under one section, matching the
                      live consultation page's Treatment rows. Always shown
                      for Ayurveda, same convention as History/Diagnosis. */}
                  {isAyurveda && (
                    <AccordionItem value="treatment" className="!border-none !pb-0">
                      <AccordionTrigger className={summarizerTriggerClassName}>
                        <div className="flex items-center gap-2 text-left">
                          <span className={summarizerHeadingClassName}>Treatment</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className={`${summarizerContentClassName} !pt-4`}>
                        <div className="space-y-4">
                          {(
                            [
                              ['treatmentMedicines', 'Medicines'],
                              ['treatmentPrinciple', 'Treatment Principle'],
                            ] as const
                          ).map(([field, label]) => {
                            // Medicines has a structured-table editing mode:
                            // whenever a table exists (edited or original),
                            // the pencil edits treatmentMedicinesTable (rows,
                            // Add/Remove) instead of collapsing to plain
                            // text. Falls back to the plain-text
                            // treatmentMedicines editor only when there's no
                            // table at all yet.
                            const editedTable = fields.treatmentMedicinesTable;
                            const displayTable =
                              editedTable && editedTable.length > 0
                                ? editedTable
                                : consultation.treatmentMedicinesTable;
                            const hasMedicinesTable = Boolean(
                              displayTable && displayTable.length > 0
                            );
                            const effectiveField: CaseSheetEditableFieldKey =
                              field === 'treatmentMedicines' && hasMedicinesTable
                                ? 'treatmentMedicinesTable'
                                : field;

                            return (
                              <div key={field}>
                                <div className="mb-2 flex min-h-[28px] items-center justify-between gap-2">
                                  <span className="font-hanken text-[16px] font-medium leading-[20px] tracking-[-0.16px] text-[#051858]">
                                    {label}
                                  </span>
                                  {canEdit && (
                                    <div
                                      className="flex shrink-0 items-center"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {renderHeaderActions(effectiveField)}
                                    </div>
                                  )}
                                </div>
                                {editingField === 'treatmentMedicinesTable' &&
                                effectiveField === 'treatmentMedicinesTable' ? (
                                  renderTreatmentMedicinesTableEditor()
                                ) : editingField === field ? (
                                  <div className={summarizerFieldContainerClassName}>
                                    <Textarea
                                      value={draftText}
                                      onChange={(_, value) => setDraftText(value)}
                                      autoResize
                                      minRows={2}
                                      placeholder={`Enter ${label.toLowerCase()}`}
                                    />
                                  </div>
                                ) : hasMedicinesTable && field === 'treatmentMedicines' ? (
                                  <>
                                    <div className={summarizerFieldContainerClassName}>
                                      <MedicinesTable medicines={displayTable!} />
                                    </div>
                                    {renderFeedbackButtons()}
                                  </>
                                ) : (
                                  <>
                                    <div className={summarizerFieldContainerClassName}>
                                      <p className={summarizerBodyClassName}>
                                        {getDisplayText(field)}
                                      </p>
                                    </div>
                                    {renderFeedbackButtons()}
                                  </>
                                )}
                              </div>
                            );
                          })}
                          {/* Diet and Lifestyle — direct rows under
                              Treatment, same level as Medicines/Treatment
                              Principle (no "Treatment Given" wrapper). */}
                          {renderAyurvedaRecommendationRow('diet')}
                          {renderAyurvedaRecommendationRow('lifestyle')}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  {/* Reference Slokas — Ayurveda-only, KB-enriched classical references from session summary */}
                  {isAyurveda &&
                    consultation.referenceSlokas &&
                    consultation.referenceSlokas.length > 0 && (
                      <AccordionItem value="referenceSlokas" className="!border-none !pb-0">
                        <AccordionTrigger className={summarizerTriggerClassName}>
                          <div className="flex items-center gap-2 text-left">
                            <span className={summarizerHeadingClassName}>Reference Slokas</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className={summarizerContentClassName}>
                          <div className="space-y-3">
                            {consultation.referenceSlokas
                              .filter(
                                (s) =>
                                  s &&
                                  s.sanskrit &&
                                  s.sanskrit.trim() &&
                                  s.meaning &&
                                  s.meaning.trim()
                              )
                              .map((sloka, idx) => (
                                <ReferenceSlokaCard key={idx} sloka={sloka} index={idx} />
                              ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )}

                  {/* Diet and Lifestyle now render as rows nested inside the
                      Treatment section (after Treatment Given) instead of
                      their own sections here. */}

                  {/* Viruddha Ahaara Section */}
                  {viruddhaAhaara !== undefined &&
                    !effectiveFieldOrder.includes('viruddha-ahaara') &&
                    renderAyurvedaRecommendationBlock('viruddha-ahaara')}

                  {/* Dosha Profile Section */}
                  {isAyurveda &&
                    !effectiveFieldOrder.includes('doshaProfile') &&
                    renderTextSection('doshaProfile', 'Astha Sthaana Pareeksha')}
                </Accordion>
              </div>{' '}
              {/* end white content card */}
              {/* Bottom Audio Recorder */}
              {audioRecorder && <div className="mt-6">{audioRecorder}</div>}
            </div>

            {/* Investigations Panel - Floating Bottom Panel (Outside scrollable div, fixed position) */}
            <div
              className="pointer-events-auto absolute bottom-4 left-4 right-4 z-20 rounded-[20px] border-2 border-white p-5 shadow-lg backdrop-blur-[15px]"
              style={{
                background:
                  'linear-gradient(135deg, rgba(255, 244, 194, 0.9) 0%, rgba(240, 243, 254, 0.9) 50%, rgba(209, 219, 253, 0.95) 100%)',
              }}
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  {/* Audio Playback Area */}
                  <div className="flex flex-1 items-center gap-2 rounded-lg border-2 border-white/70 bg-white/50 px-3 py-2 backdrop-blur-[15px]">
                    <button
                      type="button"
                      onClick={togglePlayback}
                      disabled={!effectiveAudio && !isRecoveringAudio}
                      className={`flex h-8 w-8 items-center justify-center rounded-full bg-transparent hover:bg-white/70 ${!effectiveAudio && !isRecoveringAudio ? 'cursor-not-allowed opacity-50' : ''}`}
                      aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
                    >
                      {isRecoveringAudio ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-deep-ocean border-t-transparent" />
                      ) : isPlaying ? (
                        <Pause className="h-5 w-5 text-deep-ocean" />
                      ) : (
                        <img
                          src={playIcon}
                          alt="Play"
                          className="h-5 w-5"
                          style={{
                            filter:
                              'brightness(0) saturate(100%) invert(9%) sepia(47%) saturate(2000%) hue-rotate(210deg) brightness(0.95) contrast(1.1)',
                          }}
                        />
                      )}
                    </button>
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={handleSeek}
                      role="slider"
                      aria-label="Audio progress"
                      aria-valuemin={0}
                      aria-valuemax={effectiveAudio?.durationSeconds || 0}
                      aria-valuenow={currentTime}
                      tabIndex={effectiveAudio ? 0 : -1}
                    >
                      <div className="h-1 w-full rounded-full bg-gray-300">
                        <div
                          className="h-1 rounded-full bg-deep-ocean transition-all duration-100"
                          style={{ width: `${progressPercent}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {effectiveAudio
                        ? `${formatAudioDuration(Math.floor(currentTime))} / ${formatAudioDuration(effectiveAudio.durationSeconds)}`
                        : isRecoveringAudio
                          ? 'Loading...'
                          : '--:--'}
                    </span>
                  </div>

                  {/* Smart Voice Note */}
                  {voiceNote ? (
                    <SmartVoiceNote
                      state={voiceNote.state}
                      recordingDuration={voiceNote.recordingDuration}
                      onStart={voiceNote.onStart}
                      onStop={voiceNote.onStop}
                      disabled={actions.isSaving || actions.isSaved}
                    />
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled
                      className="border-[#f8f9fb] bg-[#fefeff] text-[#243056] hover:bg-[#f0f1f5]"
                    >
                      <Mic className="h-4 w-4" />
                      Add voice note
                    </Button>
                  )}

                  {/* Autosave status */}
                  {!actions.isSaved &&
                    actions.autosaveStatus &&
                    actions.autosaveStatus !== 'idle' && (
                      <span
                        role="status"
                        className={`text-xs ${actions.autosaveStatus === 'error' ? 'text-red-600' : 'text-gray-500'}`}
                      >
                        {actions.autosaveStatus === 'saving' && 'Saving…'}
                        {actions.autosaveStatus === 'saved' &&
                          `All changes saved${
                            actions.autosaveAt
                              ? ` ${new Date(actions.autosaveAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                              : ''
                          }`}
                        {actions.autosaveStatus === 'error' && (
                          <>
                            Not saved{' '}
                            <button
                              type="button"
                              onClick={actions.onRetryAutosave}
                              className="font-medium underline"
                            >
                              Retry
                            </button>
                          </>
                        )}
                      </span>
                    )}

                  {/* Save Draft Button */}
                  {actions.onSaveDraft && !actions.isSaved && (
                    <Button
                      onClick={actions.onSaveDraft}
                      size="sm"
                      disabled={actions.isDraftSaving || actions.isSaving}
                      className="border border-[#e8eaf0] bg-[#FFFFFF] text-[#061a61] hover:bg-[#f8f9fb]"
                    >
                      <SaveDraft className="mr-2 h-[18px] w-[18px]" />
                      {actions.isDraftSaving ? 'Saving...' : 'Save Draft'}
                    </Button>
                  )}

                  {/* Save Record Button */}
                  <Button
                    onClick={actions.onSave}
                    size="sm"
                    disabled={actions.isSaving || actions.isSaved || actions.isDraftSaving}
                    className="bg-[#061a61] text-[#f8f9fb] hover:bg-[#0a2580]"
                  >
                    <SaveRecord className="mr-2 h-[14px] w-[14px]" />
                    {actions.isSaving ? 'Saving...' : actions.isSaved ? 'Saved' : 'Save Record'}
                  </Button>
                </div>

                {voiceNote?.errorMessage && (
                  <div
                    role="alert"
                    className="rounded-lg border border-[#F6CACA] bg-[#FFF3F3] px-3 py-2 text-xs font-medium text-[#B42318]"
                  >
                    Voice note recorded, but transcription could not be completed.{' '}
                    {voiceNote.errorMessage}
                  </div>
                )}
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* RIGHT PANEL - Prescription and CDSS */}
          <ResizablePanel
            defaultSize={32}
            minSize={20}
            className="rounded-3xl border border-white bg-[#E6E8EF] p-4"
          >
            <div className="flex h-full flex-col gap-6">
              {/* Prescription Block - Top 50% */}
              <div className="scrollbar-thin flex-1 overflow-y-auto">
                <div className="rounded-xl bg-[#E6E8EF] p-4">
                  {/* Prescription Header */}
                  <div className="mb-4">
                    <h2 className="font-serif text-[20px] font-normal leading-[30px] tracking-[0px] text-[#051858]">
                      Prescription
                    </h2>
                    <p className="mt-1 font-hanken text-[14px] font-medium leading-5 tracking-[-0.16px] text-[#585859]">
                      This prescription information is summarised from the conversation, by AI.
                      Verify before you save the details
                    </p>
                  </div>

                  {/* Medicines Section */}
                  <CollapsiblePrimitive.Root
                    open={expandedPrescriptionBlocks.has('medicines')}
                    onOpenChange={handlePrescriptionBlockToggle('medicines')}
                    className="mb-4 rounded-xl border border-white bg-white p-4"
                  >
                    <CollapsiblePrimitive.Trigger className="mb-3 flex w-full cursor-pointer items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Medications size={16} fill="#061A61" />
                        <h3 className="font-hanken text-[16px] font-medium text-[#051858]">
                          Medicines
                        </h3>
                      </div>
                      {expandedPrescriptionBlocks.has('medicines') ? (
                        <ChevronUp className="h-4 w-4 text-[#717072]" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-[#717072]" />
                      )}
                    </CollapsiblePrimitive.Trigger>
                    <CollapsiblePrimitive.Content className="overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                      <div>
                        {ayurvedaPrescriptionMedicines.length > 0 ? (
                          <MedicinesTable medicines={ayurvedaPrescriptionMedicines} />
                        ) : displayMedications && displayMedications.length > 0 ? (
                          <>
                            <div className="mb-2 flex items-center border-b border-gray-200 pb-1">
                              <span className="flex-1 font-hanken text-xs font-medium text-[#051858]">
                                Medicines
                              </span>
                              <span className="w-16 text-right font-hanken text-xs font-medium text-[#051858]">
                                Type
                              </span>
                              <span className="font-hankan w-24 text-right text-xs font-medium text-[#051858]">
                                Dosage
                              </span>
                            </div>
                            <ul className="space-y-2">
                              {displayMedications.map((med: SimplifiedMedication, idx: number) => {
                                return (
                                  <li
                                    key={`prescription-med-${idx}`}
                                    className="flex items-center gap-1 text-sm"
                                  >
                                    <span className="mr-1 text-[#051858]">•</span>
                                    <span className="flex-1 font-hanken text-[#585859]">
                                      {med.medication}
                                    </span>
                                    <span className="w-16 text-right font-hanken text-[#585859]">
                                      {med.medicineType || '—'}
                                    </span>
                                    <span className="w-24 text-right font-hanken text-[#585859]">
                                      {med.dosage || '—'}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No prescribed medications recorded
                          </p>
                        )}
                      </div>
                    </CollapsiblePrimitive.Content>
                  </CollapsiblePrimitive.Root>

                  {/* Investigations Section */}
                  <CollapsiblePrimitive.Root
                    open={expandedPrescriptionBlocks.has('investigations')}
                    onOpenChange={handlePrescriptionBlockToggle('investigations')}
                    className="mb-1 rounded-xl border border-white bg-white p-4"
                  >
                    <CollapsiblePrimitive.Trigger className="mb-3 flex w-full cursor-pointer items-center justify-between">
                      <div className="flex items-center gap-2">
                        <PhotoFilter size={20} fill="#061A61" />
                        <h3 className="font-hanken text-[16px] font-medium text-[#051858]">
                          Investigations
                        </h3>
                      </div>
                      {expandedPrescriptionBlocks.has('investigations') ? (
                        <ChevronUp className="h-4 w-4 text-[#717072]" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-[#717072]" />
                      )}
                    </CollapsiblePrimitive.Trigger>
                    <CollapsiblePrimitive.Content className="overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                      <div>
                        {investigationsItems.length > 0 ? (
                          <ul className="list-disc space-y-1 pl-5">
                            {investigationsItems.map((item, idx) => (
                              <li key={`investigation-${idx}`} className="text-sm text-[#585859]">
                                {item}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No investigations recorded
                          </p>
                        )}
                      </div>
                    </CollapsiblePrimitive.Content>
                  </CollapsiblePrimitive.Root>

                  {actions.onSendToInvestigation && (
                    <Button
                      className="mb-1 w-full bg-[#051858] text-white hover:bg-[#0a2470]"
                      loading={actions.isSendingToInvestigation}
                      onClick={actions.onSendToInvestigation}
                    >
                      Send to Investigation
                    </Button>
                  )}

                  {/* Print Prescription Button */}
                  <Button
                    className="w-full bg-[#051858] text-white hover:bg-[#0a2470]"
                    onClick={() => {
                      // Build full address from parts
                      const addressParts = [
                        patient.address,
                        patient.city,
                        patient.state,
                        patient.postalCode,
                        patient.country,
                      ].filter(Boolean);
                      const fullAddress = addressParts.join(', ');
                      const doctorName =
                        user?.firstName && user?.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user?.username || '';

                      const prescriptionData: PrescriptionData = {
                        pageSize: prescriptionPageSize,
                        doctorName,
                        patient: {
                          name: patient.name,
                          phone: patient.phoneNumber || undefined,
                          identifier: patient.identifier,
                          age: patient.age,
                          gender: formatGenderDisplay(patient.gender),
                          dateOfBirth: patient.dateOfBirth || undefined,
                          address: fullAddress || undefined,
                        },
                        date: new Date(consultation.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        }),
                        vitals: consultation.vitals
                          ? {
                              height: consultation.vitals.height,
                              weight: consultation.vitals.weight,
                              temperature: consultation.vitals.temperature,
                              bloodPressure: consultation.vitals.bloodPressure,
                              heartRate: consultation.vitals.heartRate,
                              respiratoryRate: consultation.vitals.respiratoryRate,
                              oxygenSaturation: consultation.vitals.oxygenSaturation,
                              hemoglobin: consultation.vitals.hemoglobin,
                              cholesterol: consultation.vitals.cholesterol,
                              bloodSugar: consultation.vitals.bloodSugar,
                            }
                          : undefined,
                        complaints: fields.chiefComplaint || '',
                        diagnosis: diagnosisText || '',
                        diagnosisCodes: displayDiagnosisCodes,
                        medications: displayMedicationsForPrescription,
                        ayurvedaMedicines: displayTreatmentMedicinesTable,
                        investigations:
                          investigationsItems.length > 0 ? investigationsItems : undefined,
                        // Strip the "Voice note transcription:" label — it's useful
                        // context on-screen but shouldn't appear on the printed prescription.
                        doctorNotes:
                          fields.doctorNotes
                            ?.split(/\n{2,}/)
                            .map((block) =>
                              block.replace(/^\s*Voice note transcription:\s*\n?/i, '').trim()
                            )
                            .filter(Boolean)
                            .join('\n\n') || undefined,
                      };
                      printPrescription(prescriptionData);
                    }}
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    Print Prescription
                  </Button>

                  {/*
                   * WhatsApp sending is intentionally hidden for now. Keep the
                   * implementation above so the feature can be re-enabled once
                   * the integration is ready for production use.
                   */}
                  {showWhatsAppAction && (
                    <Button
                      className="w-full bg-[#25D366] text-white hover:bg-[#1da851]"
                      disabled={!patient.phoneNumber || isSendingWhatsApp}
                      onClick={handleSendWhatsApp}
                    >
                      <WhatsApp className="mr-2 h-4 w-4" />
                      {isSendingWhatsApp ? 'Sending...' : 'Send to WhatsApp'}
                    </Button>
                  )}
                </div>
              </div>

              {/* CDSS Block - Bottom 50% - Always show */}
              <div className="scrollbar-thin flex-1 overflow-y-auto">
                <div className="rounded-xl bg-[#E6E8EF] p-4">
                  {/* CDSS Header */}
                  <div className="mb-3">
                    <div className="rounded-lg border border-[#eaebf3] bg-gradient-to-r from-[#fff4c2] via-[#f0f3fe] to-[#d1dbfd] p-1">
                      <div className="flex items-center gap-2 p-1">
                        <div className="flex h-6 w-6 items-center justify-center">
                          <Sparkle size={18} className="text-[#1f3b73]" />
                        </div>
                        <h2 className="font-serif text-xl leading-[30px] text-[#051858]">
                          Pranik's CDSS
                        </h2>
                      </div>
                      <p className="pl-8 text-sm font-medium leading-5 text-[#585859]">
                        Smart support system powered by AI
                      </p>
                    </div>
                  </div>

                  {/* Better Medication Suggestions */}
                  {hasBetterMedSuggestions && (
                    <div className="space-y-2">
                      {pranikCdss!.betterMedicationSuggestions!.value.map(
                        (suggestion: BetterMedicationSuggestion, idx: number) => (
                          <CDSSMedicationCard
                            key={`cdss-med-${idx}`}
                            suggestion={suggestion}
                            cardType="better"
                            onAddDrug={() => {
                              if (onAddMedication) {
                                onAddMedication({
                                  medication: suggestion.medication || '',
                                  dosage: '',
                                  frequency: '',
                                  timing: [],
                                  duration: '',
                                });
                              }
                            }}
                            onReplaceDrug={() => {
                              if (onReplaceMedication && suggestion.replace_with) {
                                onReplaceMedication(
                                  suggestion.medication_id || '',
                                  suggestion.medication || '',
                                  {
                                    medication: suggestion.replace_with || '',
                                    dosage: '',
                                    frequency: '',
                                    timing: [],
                                    duration: '',
                                  }
                                );
                              }
                            }}
                          />
                        )
                      )}
                    </div>
                  )}

                  {/* Drug Interaction Alerts Section */}
                  {hasDrugInteractionAlerts && (
                    <div className="mt-4 space-y-2">
                      {pranikCdss!.drugInteractionAlerts!.value.map(
                        (alert: DrugInteractionAlert, idx: number) => (
                          <CDSSMedicationCard
                            key={`drug-alert-${idx}`}
                            cardType="alert"
                            suggestion={{
                              medication: alert.medication,
                              medication_id: alert.medication_id,
                              explanation: alert.explanation,
                              replace_with: alert.replace_with,
                              replace_with_medication_id: alert.replace_with_medication_id,
                              source_url: alert.source_url,
                            }}
                            onAddDrug={() => {
                              // No action needed for drug interaction - use replace instead
                            }}
                            onReplaceDrug={() => {
                              if (onReplaceMedication && alert.replace_with) {
                                onReplaceMedication(
                                  alert.medication_id || '',
                                  alert.medication || '',
                                  {
                                    medication: alert.replace_with || '',
                                    dosage: '',
                                    frequency: '',
                                    timing: [],
                                    duration: '',
                                  }
                                );
                              }
                            }}
                          />
                        )
                      )}
                    </div>
                  )}

                  {/* Missed Symptom Alerts Section */}
                  {hasMissedSymptomAlerts && (
                    <div className="mt-4 space-y-2">
                      {pranikCdss!.missedSymptomAlerts!.value.map(
                        (alert: MissedSymptomAlert, idx: number) => (
                          <CDSSMedicationCard
                            key={`missed-symptom-${idx}`}
                            cardType="missed"
                            suggestion={{
                              // Show the symptom name as the main title when no medication is suggested
                              medication: alert.replace_with || alert.symptom || 'Missed Symptom',
                              medication_id: alert.replace_with_medication_id || '',
                              explanation: alert.explanation || alert.reason || '',
                              // Empty replace_with for missed symptoms - only Add drug button is shown
                              replace_with: '',
                              replace_with_medication_id: '',
                              source_url: alert.source_url,
                            }}
                            onAddDrug={() => {
                              if (onAddMedication && alert.replace_with) {
                                onAddMedication({
                                  medication: alert.replace_with || '',
                                  dosage: '',
                                  frequency: '',
                                  timing: [],
                                  duration: '',
                                });
                              }
                            }}
                          />
                        )
                      )}
                    </div>
                  )}

                  {/* Allergy Conflict Alerts Section */}
                  {hasAllergyConflictAlerts && (
                    <div className="mt-4 space-y-2">
                      {pranikCdss!.allergyConflictAlerts!.value.map(
                        (alert: AllergyConflictAlert, idx: number) => (
                          <CDSSMedicationCard
                            key={`allergy-alert-${idx}`}
                            cardType="allergy"
                            suggestion={{
                              // Show the medication that conflicts with allergy as the heading
                              medication: alert.medication,
                              medication_id: alert.medication_id,
                              explanation: alert.explanation,
                              replace_with: alert.replace_with,
                              replace_with_medication_id: alert.replace_with_medication_id,
                              source_url: alert.source_url,
                              allergy: alert.allergy,
                              allergy_timestamp: alert.allergy_timestamp,
                              conflicting_medication: alert.conflicting_medication,
                              conflicting_medication_timestamp:
                                alert.conflicting_medication_timestamp,
                              severity: alert.severity,
                              evidence_confidence: alert.evidence_confidence,
                              source_phrase: alert.source_phrase,
                            }}
                            onAddDrug={() => {
                              if (onAddMedication && alert.replace_with) {
                                onAddMedication({
                                  medication: alert.replace_with || '',
                                  dosage: '',
                                  frequency: '',
                                  timing: [],
                                  duration: '',
                                });
                              }
                            }}
                            onReplaceDrug={() => {
                              if (onReplaceMedication && alert.replace_with) {
                                onReplaceMedication(
                                  alert.medication_id || '',
                                  alert.medication || '',
                                  {
                                    medication: alert.replace_with || '',
                                    dosage: '',
                                    frequency: '',
                                    timing: [],
                                    duration: '',
                                  }
                                );
                              }
                            }}
                          />
                        )
                      )}
                    </div>
                  )}

                  {/* Empty state when no CDSS data */}
                  {!hasCdssData && (
                    <p className="text-sm text-muted-foreground">No CDSS suggestions available</p>
                  )}

                  {/* Feedback thumbs - only show if there are suggestions */}
                  {hasBetterMedSuggestions && (
                    <div className="mt-3 flex items-center gap-2.5 px-1">
                      <Button variant="ghost" size="icon" className="h-[14px] w-[14px]">
                        <ThumbUp size={14} fill="#969AAC" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-[14px] w-[14px]">
                        <ThumbDown size={14} fill="#969AAC" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
}
